import React, { useEffect, useState, Fragment, createElement } from 'react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Scan, CheckCircle, XCircle, Search, Users, FileSpreadsheet, Download, BarChart3, ClipboardList, ChevronDown, ChevronUp, Package, Star, Calendar, Plus, Minus, Trash2, RefreshCw, Copy, Ticket, Tag, Clock, MapPin, PieChart, Settings2, X, DatabaseBackup, FileJson, ShieldCheck, Brain, Camera, QrCode, AlertCircle, Power, CalendarClock } from 'lucide-react';
import { useStore, BATCH_CODES, generateDropCode, VERIFICATION_METHODS, normalizeVerificationMethod, findEventByCode, normalizeCode, LEVEL_NAMES, pointsForLevel, RECURRENCE_FREQUENCIES, WEEKDAY_LABELS, normalizeRecurrence, describeRecurrence, isRecurring, nextOccurrence, redeemedCount, partnerLocationLabel, isDropEnabled } from '../lib/store';
import type { DropQuestion, DropProduct, DropTargeting, DropRecurrence, VerificationMethod } from '../lib/store';
import { fetchAdminSnapshot, testSheetWrite, patchCatch, bulkSeed, deleteAllRows, fetchAdminAuth, syncAdminAuth, DEFAULT_ADMIN_AUTH } from '../lib/supabase';
import type { AdminUser, AdminCatch, AdminReview, AdminAuthConfig } from '../lib/supabase';
import { buildMockDrops, buildMockUsers, brandMeta, DEFAULT_DROP_CONFIG, DEFAULT_USER_CONFIG, CITY_OPTIONS, AGE_OPTIONS, INTEREST_OPTIONS, type DropGenConfig, type UserGenConfig } from '../lib/seed';
import { SmartDropEngine } from '../components/SmartDropEngine';
import type { RecognitionResult } from '../lib/productRecognition';
import { onDataChanged } from '../lib/liveSync';
// Audience-targeting option pools and occupation list for the Drop editor.
const OCCUPATION_OPTIONS = ['Student', 'Employed full-time', 'Employed part-time', 'Self-employed', 'Job seeking', 'Parental leave', 'Retired'];
const ACTIVITY_OPTIONS: {
  value: 'new' | 'returning' | 'loyal';
  label: string;
}[] = [{
  value: 'new',
  label: 'New (1 catch)'
}, {
  value: 'returning',
  label: 'Returning (2-3)'
}, {
  value: 'loyal',
  label: 'Loyal (4+)'
}];
import { AdminVerifyMember } from '../components/AdminVerifyMember';
import { DropQrCodes } from '../components/DropQrCodes';
import { BrandDashboard } from '../components/BrandDashboard';
import { BehaviorInsights } from '../components/BehaviorInsights';
import type { JoinedReviewRow } from '../components/BehaviorInsights';
import { QrScanner } from '../components/QrScanner';
import { TestBot } from '../components/TestBot';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
function ProductEditor({
  products,
  onChange,
  max = 4
}: {
  products: DropProduct[];
  onChange: (ps: DropProduct[]) => void;
  max?: number;
}) {
  const addProduct = () => {
    if (products.length >= max) return;
    onChange([...products, {
      name: '',
      brand: ''
    }]);
  };
  const updateProduct = (index: number, updates: Partial<DropProduct>) => {
    onChange(products.map((p, i) => i === index ? {
      ...p,
      ...updates
    } : p));
  };
  const removeProduct = (index: number) => {
    onChange(products.filter((_, i) => i !== index));
  };
  return <div className="space-y-4" data-id="element-788" data-mp="1e7rdqzfcbiak">
      {products.map((p, i) => <div key={i} className="bg-gray-800 p-4 rounded-lg border border-gray-700 relative" data-id="element-789" data-mp="1j6uh8ffo9ggf" data-mpi={i}>
          <button onClick={() => removeProduct(i)} className="absolute top-3 right-3 text-gray-400 hover:text-red-400" type="button" data-id="element-790" data-mp="1488uymb622k3">
            <X className="w-4 h-4" data-id="element-791" data-mp="3oyzvryufbv9a" />
          </button>
          <div className="space-y-3" data-id="element-792" data-mp="3p1m440p52esk">
            <div data-id="element-793" data-mp="3tymbcexq5nl9">
              <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-794" data-mp="3tz5hyzhxcpjz">
                Product {i + 1} Name
              </label>
              <Input value={p.name} onChange={e => updateProduct(i, {
            name: e.target.value
          })} placeholder="e.g. NOCCO Limón" className="bg-gray-900 border-gray-700 text-white" data-id="element-795" data-mp="e1xjxxlhpnxs" />
            </div>
            <div data-id="element-796" data-mp="wxxeng5ur5l2">
              <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-797" data-mp="wygriignb3s8">
                Brand
              </label>
              <Input value={p.brand} onChange={e => updateProduct(i, {
            brand: e.target.value
          })} placeholder="e.g. NOCCO" className="bg-gray-900 border-gray-700 text-white" data-id="element-798" data-mp="n0ssvhfx5c4s" />
            </div>
          </div>
        </div>)}
      {products.length < max && <Button variant="secondary" onClick={addProduct} type="button" className="w-full border-dashed border-gray-700 text-gray-400 hover:text-white" data-id="element-799" data-mp="1iaejxd9h2lxm">
          <Plus className="w-4 h-4 mr-2" data-id="element-800" data-mp="1s7kbemm00eyd" /> Add Product
        </Button>}
    </div>;
}
function QuestionEditor({
  questions,
  onChange,
  max = 10
}: {
  questions: DropQuestion[];
  onChange: (qs: DropQuestion[]) => void;
  max?: number;
}) {
  const addQuestion = () => {
    if (questions.length >= max) return;
    onChange([...questions, {
      id: Math.random().toString(36).substr(2, 9),
      label: '',
      type: 'text'
    }]);
  };
  const updateQuestion = (id: string, updates: Partial<DropQuestion>) => {
    onChange(questions.map(q => q.id === id ? {
      ...q,
      ...updates
    } : q));
  };
  const removeQuestion = (id: string) => {
    onChange(questions.filter(q => q.id !== id));
  };
  return <div className="space-y-4" data-id="element-801" data-mp="djpj0lyunp82">
      {questions.map((q, i) => <div key={q.id} className="bg-gray-800 p-4 rounded-lg border border-gray-700 relative" data-id="element-802" data-mp="9huhbgd0b9tr" data-mpi={i}>
          <button onClick={() => removeQuestion(q.id)} className="absolute top-3 right-3 text-gray-400 hover:text-red-400" data-id="element-803" data-mp="nhww2tq0pn4s">
            <X className="w-4 h-4" data-id="element-804" data-mp="whjzfl2b530h" />
          </button>
          <div className="space-y-3" data-id="element-805" data-mp="wg8w1mcrxx9w">
            <div data-id="element-806" data-mp="sg6rn5fnmpqx">
              <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-807" data-mp="sgqcatwovs8n">
                Question {i + 1}
              </label>
              <Input value={q.label} onChange={e => updateQuestion(q.id, {
            label: e.target.value
          })} placeholder="e.g. What is your favorite flavor?" className="bg-gray-900 border-gray-700 text-white" data-id="element-808" data-mp="166tr4zt8ojo2" />
            </div>
            <div data-id="element-809" data-mp="1utjduyohdx3x">
              <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-810" data-mp="1uszrn5rssmk3">
                Type
              </label>
              <select value={q.type} onChange={e => updateQuestion(q.id, {
            type: e.target.value as 'text' | 'choice'
          })} className="w-full p-2 rounded-lg bg-gray-900 border border-gray-700 text-white outline-none focus:border-blue-500" data-id="element-811" data-mp="1yrqs9mvh2mby">
                <option value="text" data-id="element-812" data-mp="1fwijsoithq2a">Text Input</option>
                <option value="choice" data-id="element-813" data-mp="124lt8owsro80">Multiple Choice</option>
              </select>
            </div>
            {q.type === 'choice' && <div data-id="element-814" data-mp="1735fkqfaz20w">
                <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-815" data-mp="170j80qsewmio">
                  Options (comma separated)
                </label>
                <Input value={(q.options || []).join(', ')} onChange={e => updateQuestion(q.id, {
            options: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
          })} placeholder="e.g. Apple, Orange, Banana" className="bg-gray-900 border-gray-700 text-white" data-id="element-816" data-mp="x3en0uaayx3g" />
              </div>}
          </div>
        </div>)}
      {questions.length < max && <Button variant="secondary" onClick={addQuestion} className="w-full border-dashed border-gray-700 text-gray-400 hover:text-white" data-id="element-817" data-mp="ovveo5gaxvq2">
          <Plus className="w-4 h-4 mr-2" data-id="element-818" data-mp="fxrpmz63ygti" /> Add Question
        </Button>}
    </div>;
}
/** Multi-select chip group for audience targeting in the Drop editor. */
function ChipMultiSelect({
  options,
  selected,
  onToggle
}: {
  options: {
    value: string;
    label: string;
  }[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return <div className="flex flex-wrap gap-2" data-id="element-819" data-mp="t6xyt2o0t5t">
      {options.map(opt => {
      const active = selected.includes(opt.value);
      return <button key={opt.value} type="button" onClick={() => onToggle(opt.value)} aria-pressed={active} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${active ? 'bg-brand-blue text-white' : 'bg-gray-800 text-gray-300 hover:text-white border border-gray-700'}`} data-id="element-820" data-mp="5t2yujg2g4h5">
            {opt.label}
          </button>;
    })}
    </div>;
}
/** Editor for a drop's recurring schedule (frequency, interval, weekdays, end). */
function RecurrenceEditor({
  recurrence,
  onChange
}: {
  recurrence: DropRecurrence;
  onChange: (r: DropRecurrence) => void;
}) {
  const r = normalizeRecurrence(recurrence);
  const toggleWeekday = (d: number) => {
    const cur = r.weekdays || [];
    onChange({
      ...r,
      weekdays: cur.includes(d) ? cur.filter(x => x !== d) : [...cur, d].sort((a, b) => a - b)
    });
  };
  return <div className="space-y-4" data-id="element-821" data-mp="hhns29w98zn8">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-822" data-mp="hhe22mb7mshj">
        <div data-id="element-823" data-mp="lj911k5o4ts5">
          <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-824" data-mp="liz8ppx5iaja">
            Repeats
          </label>
          <select value={r.frequency} onChange={e => onChange({
          ...r,
          frequency: e.target.value as DropRecurrence['frequency']
        })} className="w-full rounded-lg bg-gray-800 border border-gray-700 text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue" data-id="element-825" data-mp="3kz5qjqq50uaz">
            {RECURRENCE_FREQUENCIES.map(f => <option key={f.value} value={f.value} data-id="element-826" data-mp="32621kinv75mp">
                {f.label}
              </option>)}
          </select>
        </div>
        {r.frequency !== 'none' && <div data-id="element-827" data-mp="2ynuujf4knz3u">
            <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-828" data-mp="2ynl0nggep7sv">
              Every (interval)
            </label>
            <Input type="number" min="1" value={r.interval || 1} onChange={e => onChange({
          ...r,
          interval: Math.max(1, Number(e.target.value))
        })} className="bg-gray-800 border-gray-700 text-white" data-id="element-829" data-mp="2pohiqfsz6yfy" />
          </div>}
      </div>

      {r.frequency === 'weekly' && <div data-id="element-830" data-mp="25z74754yujjf">
          <label className="text-xs text-gray-500 uppercase font-bold mb-1.5 block" data-id="element-831" data-mp="25yxab6gsvs8g">
            On days (optional — leave empty for the start weekday)
          </label>
          <div className="flex flex-wrap gap-2" data-id="element-832" data-mp="1x028e6ksh4ay">
            {WEEKDAY_LABELS.map((label, d) => {
          const active = (r.weekdays || []).includes(d);
          return <button key={label} type="button" onClick={() => toggleWeekday(d)} aria-pressed={active} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${active ? 'bg-brand-blue text-white' : 'bg-gray-800 text-gray-300 hover:text-white border border-gray-700'}`} data-id="element-833" data-mp="1id3hqdtradxa" data-mpi={d}>
                  {label}
                </button>;
        })}
          </div>
        </div>}

      {r.frequency !== 'none' && <div data-id="element-834" data-mp="1rekvhpjoy3it">
          <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-835" data-mp="1reuolm018qsq">
            Ends on (optional)
          </label>
          <Input type="date" value={r.until || ''} onChange={e => onChange({
        ...r,
        until: e.target.value || undefined
      })} className="bg-gray-800 border-gray-700 text-white" data-id="element-836" data-mp="1wccwra6871zo" />
        </div>}

      <p className="text-[11px] text-gray-500" data-id="element-837" data-mp="dcwvl4jqmhqa">
        {r.frequency === 'none' ? 'This drop runs once on its start date.' : `Schedule: ${describeRecurrence(r)}.`}
      </p>
    </div>;
}

/**
 * The availability model for a drop. Simple by default:
 *  - `enabled` ON  → the drop is live RIGHT NOW, indefinitely. No date/time
 *    needed. This is the fast path the admin uses ~99% of the time.
 *  - `scheduled`   → OPTIONAL. Flip it on to constrain the drop to a specific
 *    date with a start + end time.
 */
export interface AvailabilityState {
  enabled: boolean;
  scheduled: boolean;
  date: string;
  startTime: string;
  endTime: string;
}
export const DEFAULT_AVAILABILITY: AvailabilityState = {
  enabled: true,
  scheduled: false,
  date: '',
  startTime: '',
  endTime: ''
};

/** Resolve availability state into the drop's { date, time, recurrence, enabled }. */
export function resolveAvailability(a: AvailabilityState): {
  date: string;
  time: string;
  recurrence: DropRecurrence;
  enabled: boolean;
} {
  // Not scheduled → no date/time at all. An enabled, date-less drop is treated
  // as always-active by getDropWindowStatus, so ON = live immediately.
  if (!a.scheduled) {
    return {
      date: '',
      time: '',
      recurrence: {
        frequency: 'none'
      },
      enabled: a.enabled
    };
  }
  const time = a.startTime && a.endTime ? `${a.startTime} - ${a.endTime}` : a.startTime || '';
  return {
    date: a.date,
    time,
    recurrence: {
      frequency: 'none'
    },
    enabled: a.enabled
  };
}
function AvailabilityEditor({
  value,
  onChange
}: {
  value: AvailabilityState;
  onChange: (a: AvailabilityState) => void;
}) {
  const set = (patch: Partial<AvailabilityState>) => onChange({
    ...value,
    ...patch
  });
  return <div className="space-y-3" data-id="element-838" data-mp="37vqqo5eh37gk">
      {/* One-click master ON/OFF — ON means live immediately. */}
      <button type="button" onClick={() => set({
      enabled: !value.enabled
    })} aria-pressed={value.enabled} className={`w-full flex items-center justify-between rounded-xl border p-4 transition-colors ${value.enabled ? 'bg-green-500/10 border-green-500/40' : 'bg-gray-800 border-gray-700'}`} data-id="element-839" data-mp="3ct8r17efcecy">
        <span className="flex items-center gap-3" data-id="element-840" data-mp="m9fsq0vpu7z0">
          <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${value.enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'}`} data-id="element-841" data-mp="1jw54h2g7zyy2">
            <Power className="w-5 h-5" data-id="element-842" data-mp="1a50pw3ugig3r" />
          </span>
          <span className="text-left" data-id="element-843" data-mp="157ie85p21ms2">
            <span className="block text-sm font-bold text-white" data-id="element-844" data-mp="156h1zxwayu8r">
              {value.enabled ? value.scheduled ? 'Available — ON (scheduled)' : 'Available now — live immediately' : 'Turned OFF'}
            </span>
            <span className="block text-xs text-gray-400" data-id="element-845" data-mp="3s3i2jv4yt3qk">
              {value.enabled ? 'One click to turn availability off' : 'One click to make it live right now'}
            </span>
          </span>
        </span>
        <span className={`relative w-12 h-7 rounded-full transition-colors ${value.enabled ? 'bg-green-500' : 'bg-gray-600'}`} data-id="element-846" data-mp="3ibugk9r6gewh">
          <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${value.enabled ? 'left-6' : 'left-1'}`} data-id="element-847" data-mp="39bp8ryi2nehs" />
        </span>
      </button>

      {/* OPTIONAL specific date + time window */}
      <button type="button" onClick={() => set({
      scheduled: !value.scheduled
    })} aria-pressed={value.scheduled} className="w-full flex items-center justify-between rounded-xl border border-gray-700 bg-gray-800/60 px-4 py-3 hover:border-gray-600 transition-colors" data-id="element-848" data-mp="34dnppdknbf6b">
        <span className="flex items-center gap-2.5" data-id="element-849" data-mp="2n4bnbk50q2ix">
          <CalendarClock className="w-4 h-4 text-brand-blue" data-id="element-850" data-mp="2n2qvw3e02zpa" />
          <span className="text-sm font-medium text-white" data-id="element-851" data-mp="2i6tedtr03nax">
            Set a specific date &amp; time
          </span>
          <span className="text-[11px] text-gray-500" data-id="element-852" data-mp="2ww7sff5iuswn">(optional)</span>
        </span>
        <span className={`relative w-10 h-6 rounded-full transition-colors ${value.scheduled ? 'bg-brand-blue' : 'bg-gray-600'}`} data-id="element-853" data-mp="2s368gsqgk367">
          <span className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-all ${value.scheduled ? 'left-5' : 'left-1'}`} data-id="element-854" data-mp="30x1d7zno3rlb" />
        </span>
      </button>

      {value.scheduled && <div className="rounded-xl bg-gray-800/60 border border-gray-700 p-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-855" data-mp="3av7csam04ln0">
          <div data-id="element-856" data-mp="1lhcbtnnbehdg">
            <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-857" data-mp="1ggob2a9aam61">
              Date
            </label>
            <Input type="date" value={value.date} onChange={e => set({
          date: e.target.value
        })} className="bg-gray-800 border-gray-700 text-white" data-id="element-858" data-mp="uo0qt7gipsjs" />
          </div>
          <div data-id="element-859" data-mp="14du3ycwb6fq5">
            <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-860" data-mp="14f5482qjp5ci">
              Start time
            </label>
            <Input type="time" value={value.startTime} onChange={e => set({
          startTime: e.target.value
        })} className="bg-gray-800 border-gray-700 text-white" data-id="element-861" data-mp="bomwupisghta" />
          </div>
          <div data-id="element-862" data-mp="lkzntfmy8hkc">
            <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-863" data-mp="longkckkkzy0">
              End time
            </label>
            <Input type="time" value={value.endTime} onChange={e => set({
          endTime: e.target.value
        })} className="bg-gray-800 border-gray-700 text-white" data-id="element-864" data-mp="1x1zp30r9y6qi" />
          </div>
        </div>}
    </div>;
}
type Tab = 'verify' | 'users' | 'reviews' | 'insights' | 'stats' | 'drops' | 'demographics' | 'referrals' | 'partners' | 'tools';
function downloadCSV(filename: string, csvContent: string) {
  const blob = new Blob(['\uFEFF' + csvContent], {
    type: 'text/csv;charset=utf-8;'
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}
function downloadJSON(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json;charset=utf-8;'
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}
/** Escape a value for safe inclusion in a CSV cell. */
function csvCell(v: unknown): string {
  return `"${String(v ?? '').replace(/"/g, '""')}"`;
}
const getDistribution = (data: any[], key: string | ((item: any) => string | string[])) => {
  const counts: Record<string, number> = {};
  data.forEach(item => {
    const val = typeof key === 'function' ? key(item) : item[key];
    if (Array.isArray(val)) {
      val.forEach(v => {
        if (v && v.trim() !== '') counts[v] = (counts[v] || 0) + 1;
      });
    } else if (val && typeof val === 'string' && val.trim() !== '') {
      counts[val] = (counts[val] || 0) + 1;
    } else if (val && typeof val !== 'string') {
      counts[String(val)] = (counts[String(val)] || 0) + 1;
    }
  });
  return Object.entries(counts).sort((a, b) => b[1] - a[1]);
};
export function AdminPage() {
  const [tab, setTab] = useState<Tab>('stats');
  const [scanResult, setScanResult] = useState('');
  const [verificationStatus, setVerificationStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [verifiedUser, setVerifiedUser] = useState<any>(null);
  // Camera QR scanner (Verify tab).
  const [showScanner, setShowScanner] = useState(false);
  const [scanVerdict, setScanVerdict] = useState<{
    kind: 'valid' | 'redeemed' | 'invalid' | 'drop';
    title: string;
    detail?: string;
  } | null>(null);
  const [expandedUserPhone, setExpandedUserPhone] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showAddDrop, setShowAddDrop] = useState(false);
  // Inline validation error for the create-drop form (null = no error).
  const [addDropError, setAddDropError] = useState<string | null>(null);
  // Login mode: full admin vs a brand-only data view.
  const [loginMode, setLoginMode] = useState<'admin' | 'brand'>('admin');
  const [brandName, setBrandName] = useState('');
  const [brandPassword, setBrandPassword] = useState('');
  const [loggedInBrand, setLoggedInBrand] = useState<string | null>(null);
  const [loginError, setLoginError] = useState('');
  // Login credentials live in Supabase (app_config "adminAuth"). Loaded on
  // mount; falls back to the built-in defaults until/if the fetch resolves so
  // the platform is never locked out.
  const [authConfig, setAuthConfig] = useState<AdminAuthConfig>(DEFAULT_ADMIN_AUTH);
  useEffect(() => {
    let active = true;
    fetchAdminAuth().then(cfg => {
      if (active) setAuthConfig(cfg);
    });
    return () => {
      active = false;
    };
  }, []);
  // Reviews tab: filter the brand whose product reviews are shown ('' = all).
  const [reviewBrandFilter, setReviewBrandFilter] = useState('');
  // Live SheetDB state
  const [sheetUsers, setSheetUsers] = useState<AdminUser[]>([]);
  const [sheetCatches, setSheetCatches] = useState<AdminCatch[]>([]);
  const [sheetReviews, setSheetReviews] = useState<AdminReview[]>([]);
  const [isLoadingSheet, setIsLoadingSheet] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  // SheetDB connection test
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    status: number;
    body: string;
  } | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const runConnectionTest = async () => {
    setIsTesting(true);
    setTestResult(null);
    const result = await testSheetWrite();
    setTestResult(result);
    setIsTesting(false);
    // Refresh so a successful test row shows up immediately.
    if (result.ok) loadSheetData();
  };
  // New drop form state
  const [newDrop, setNewDrop] = useState({
    title: '',
    brand: '',
    campaign: '',
    verificationMethod: 'both' as VerificationMethod,
    description: '',
    address: '',
    totalBoxes: 100,
    requiredLevel: LEVEL_NAMES[0],
    brandLogo: '',
    instagramUrl: '',
    websiteUrl: ''
  });
  // Audience targeting for the drop being created.
  const [newDropTargeting, setNewDropTargeting] = useState<DropTargeting>({});
  // Availability for the drop being created (on/off, rolling vs planned schedule).
  const [newDropAvailability, setNewDropAvailability] = useState<AvailabilityState>(DEFAULT_AVAILABILITY);
  // Per-drop refill amount inputs (restock controls on each drop card).
  const [refillAmounts, setRefillAmounts] = useState<Record<string, number>>({});
  // Questions attached to the drop being created (saved with the drop).
  const [newDropQuestions, setNewDropQuestions] = useState<DropQuestion[]>([]);
  // Products attached to the drop being created.
  const [newDropProducts, setNewDropProducts] = useState<DropProduct[]>([]);
  // Brief "Saved" confirmation after a live per-drop edit autosaves.
  const [savedDropId, setSavedDropId] = useState<string | null>(null);
  // Seed / test-data tooling state.
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedProgress, setSeedProgress] = useState<{
    done: number;
    total: number;
  }>({
    done: 0,
    total: 0
  });
  const [seedLog, setSeedLog] = useState<string[]>([]);
  // Configurable mock-data generators (drops/campaigns + users/funnel).
  const [dropConfig, setDropConfig] = useState<DropGenConfig>({
    ...DEFAULT_DROP_CONFIG
  });
  const [userConfig, setUserConfig] = useState<UserGenConfig>({
    ...DEFAULT_USER_CONFIG
  });
  const toggleInList = (list: string[] | undefined, value: string) => {
    const arr = list || [];
    return arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
  };
  const flashSaved = (id: string) => {
    setSavedDropId(id);
    window.setTimeout(() => setSavedDropId(cur => cur === id ? null : cur), 1500);
  };
  const [showRecurringEditor, setShowRecurringEditor] = useState(false);
  const [editingDropQs, setEditingDropQs] = useState<string | null>(null);
  // Partner code form state — a partner is a physical distribution location.
  const [showAddPartner, setShowAddPartner] = useState(false);
  const [newPartner, setNewPartner] = useState({
    code: '',
    name: '',
    type: 'brand' as 'brand' | 'event',
    address: '',
    city: '',
    postalCode: ''
  });
  const {
    reservations: localReservations,
    // Keep local for Verify tab
    events,
    registeredUsers,
    redeemReservation,
    addEvent,
    updateEvent,
    refillDrop,
    deleteEvent,
    activeEventId,
    activeBatchCode,
    recurringQuestions,
    setRecurringQuestions,
    partnerCodes,
    setPartnerCodes,
    resetStore,
    dropWriteError,
    clearDropWriteError
  } = useStore();
  // "Erase all data" tool state (clears the sheet + local app data for go-live).
  const [isErasing, setIsErasing] = useState(false);
  const [eraseConfirm, setEraseConfirm] = useState('');
  // Security: change the Supabase-stored admin/brand/partner login credentials.
  const [securityForm, setSecurityForm] = useState({
    adminPassword: '',
    brandPassword: '',
    partnerPassword: ''
  });
  const [isSavingSecurity, setIsSavingSecurity] = useState(false);
  const [securitySaved, setSecuritySaved] = useState(false);
  const handleSaveSecurity = async () => {
    const next = {
      adminPassword: securityForm.adminPassword.trim() || authConfig.adminPassword,
      brandPassword: securityForm.brandPassword.trim() || authConfig.brandPassword,
      partnerPassword: securityForm.partnerPassword.trim() || authConfig.partnerPassword
    };
    setIsSavingSecurity(true);
    await syncAdminAuth(next);
    setAuthConfig(next);
    setSecurityForm({
      adminPassword: '',
      brandPassword: '',
      partnerPassword: ''
    });
    setIsSavingSecurity(false);
    setSecuritySaved(true);
    window.setTimeout(() => setSecuritySaved(false), 2000);
  };
  const handleEraseAll = async () => {
    setIsErasing(true);
    const ok = await deleteAllRows();
    if (ok) {
      resetStore();
      setSheetUsers([]);
      setSheetCatches([]);
      setSheetReviews([]);
      setLastRefreshed(new Date());
    } else {
      window.alert('Could not erase the sheet data. Check the connection and try again.');
    }
    setEraseConfirm('');
    setIsErasing(false);
  };
  // Drop lookup: admin enters a drop's code to reveal its date, brand and
  // participants.
  const [lookupCode, setLookupCode] = useState('');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const copyCode = (code: string) => {
    navigator.clipboard?.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 1800);
    }, () => {});
  };
  // Resolve the drop matching the entered code (case-insensitive, trimmed).
  const lookedUpDrop = lookupCode.trim().length > 0 ? events.find(e => (e.code || '').toUpperCase() === lookupCode.trim().toUpperCase()) : undefined;
  /**
   * Everyone who joined a given drop. Combines synced catches (matched by the
   * drop's title) with locally-recorded reservations (matched by event id),
   * deduped by user code, and enriches each with the registered user's name.
   */
  const participantsForDrop = (drop: {
    id: string;
    title: string;
  }) => {
    type P = {
      phone: string;
      name: string;
      code: string;
      status: string;
      when: number;
    };
    const byCode = new Map<string, P>();
    const nameFor = (phone: string) => sheetUsers.find(u => u.phone === phone)?.name || registeredUsers.find(u => u.phone === phone)?.name || 'Unknown';
    sheetCatches.filter(c => c.eventTitle === drop.title).forEach(c => {
      const key = c.userCode || `${c.userId}-${c.timestamp}`;
      byCode.set(key, {
        phone: c.userId,
        name: nameFor(c.userId),
        code: c.userCode,
        status: c.status,
        when: c.timestamp
      });
    });
    localReservations.filter(r => r.eventId === drop.id).forEach(r => {
      const key = r.userCode || `${r.userId}-${r.timestamp}`;
      if (!byCode.has(key)) byCode.set(key, {
        phone: r.userId,
        name: nameFor(r.userId),
        code: r.userCode,
        status: r.status,
        when: r.timestamp
      });
    });
    return Array.from(byCode.values()).sort((a, b) => b.when - a.when);
  };
  const loadSheetData = async () => {
    setIsLoadingSheet(true);
    const data = await fetchAdminSnapshot();
    setSheetUsers(data.users);
    setSheetCatches(data.catches);
    setSheetReviews(data.reviews);
    setLastRefreshed(new Date());
    setIsLoadingSheet(false);
  };
  useEffect(() => {
    if (isAuthenticated || loggedInBrand) {
      loadSheetData();
      const interval = setInterval(loadSheetData, 10000); // Poll every 10s
      // Instant refresh when data changes on any other surface.
      const unsub = onDataChanged(() => loadSheetData());
      return () => {
        clearInterval(interval);
        unsub();
      };
    }
  }, [isAuthenticated, loggedInBrand]);
  const activeRound = BATCH_CODES.find(b => b.code === activeBatchCode)?.round ?? '';
  // Map question ids → human-readable labels so collected answers can be shown
  // with their question text (recurring-user questions + every drop's own
  // questions). Unknown ids fall back to the raw id.
  const questionLabelMap = (() => {
    const map: Record<string, string> = {};
    (recurringQuestions || []).forEach(q => {
      map[q.id] = q.label;
    });
    events.forEach(e => (e.customQuestions || []).forEach(q => {
      map[q.id] = q.label;
    }));
    return map;
  })();
  // Resolve a display name for a phone from synced users or the local store.
  const nameForPhone = (phone: string) => sheetUsers.find(u => u.phone === phone)?.name || registeredUsers.find(u => u.phone === phone)?.name || 'User';
  // --- VERIFY / UNLOCK ---
  // Staff enter the user's reserve code to unlock their box. The reservation may
  // live ONLY on the user's device (local store) OR — for users on another
  // device — only in the synced sheet. We handle both so unlock always works.
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = scanResult.trim().toUpperCase();
    if (!code) {
      setVerificationStatus('error');
      return;
    }
    // 1) Same-device reservation (admin's own local store).
    const localRes = localReservations.find(r => r.id === scanResult || r.userCode.toUpperCase() === code);
    if (localRes) {
      const event = events.find(ev => ev.id === localRes.eventId);
      const alreadyDone = localRes.status === 'redeemed' || localRes.status === 'reviewed';
      if (!alreadyDone) redeemReservation(localRes.id); // patches the sheet too
      setVerifiedUser({
        type: 'reservation',
        name: nameForPhone(localRes.userId),
        phone: localRes.userId,
        event: event?.title,
        brand: event?.brand,
        status: alreadyDone ? localRes.status : 'redeemed',
        alreadyDone
      });
      setVerificationStatus('success');
      loadSheetData();
      return;
    }
    // 2) Cross-device reservation — find it in the synced sheet by user code.
    const sheetRes = sheetCatches.find(c => c.userCode.toUpperCase() === code);
    if (sheetRes) {
      const alreadyDone = sheetRes.status === 'redeemed' || sheetRes.status === 'reviewed';
      if (!alreadyDone) {
        await patchCatch(sheetRes.id, {
          status: 'redeemed'
        });
        setSheetCatches(prev => prev.map(c => c.id === sheetRes.id ? {
          ...c,
          status: 'redeemed'
        } : c));
      }
      setVerifiedUser({
        type: 'reservation',
        name: nameForPhone(sheetRes.userId),
        phone: sheetRes.userId,
        event: sheetRes.eventTitle,
        brand: sheetRes.eventBrand,
        status: alreadyDone ? sheetRes.status : 'redeemed',
        alreadyDone
      });
      setVerificationStatus('success');
      loadSheetData();
      return;
    }
    setVerificationStatus('error');
  };
  // --- CAMERA SCANNER ---
  // Pull a usable code out of whatever the QR encodes: a deep-link URL with a
  // ?drop=/?code= param, or a raw code string.
  const extractCode = (raw: string): string => {
    const text = (raw || '').trim();
    try {
      const url = new URL(text);
      const param = url.searchParams.get('drop') || url.searchParams.get('code') || url.searchParams.get('userCode');
      if (param) return param.trim();
    } catch {
      /* not a URL — fall through */
    }
    return text;
  };
  // Validate a scanned code and produce a clear verdict: a user's redemption
  // code (valid / already redeemed), a recognized drop QR, or invalid.
  const handleScannedCode = async (raw: string) => {
    // Stop the camera as soon as we have something to evaluate.
    setShowScanner(false);
    const code = extractCode(raw);
    const upper = code.toUpperCase();
    if (!code) {
      setScanVerdict({
        kind: 'invalid',
        title: 'Nothing detected'
      });
      return;
    }
    // 1) Match a user's redemption (user) code in the synced catches.
    const match = sheetCatches.find(c => c.userCode.toUpperCase() === upper);
    if (match) {
      const who = nameForPhone(match.userId);
      const product = match.eventBrand || match.eventTitle || 'Catch';
      const already = match.status === 'redeemed' || match.status === 'reviewed';
      if (already) {
        setScanVerdict({
          kind: 'redeemed',
          title: 'Already redeemed',
          detail: `${who} • ${product} • status: ${match.status}`
        });
        return;
      }
      await patchCatch(match.id, {
        status: 'redeemed'
      });
      setSheetCatches(prev => prev.map(c => c.id === match.id ? {
        ...c,
        status: 'redeemed'
      } : c));
      setScanVerdict({
        kind: 'valid',
        title: 'Valid — catch unlocked',
        detail: `${who} • ${product}`
      });
      loadSheetData();
      return;
    }
    // 2) Recognized as a drop QR (drop code) but not a user redemption code.
    const drop = findEventByCode(events, code);
    if (drop) {
      setScanVerdict({
        kind: 'drop',
        title: 'Drop QR recognized',
        detail: `${drop.brand} • ${drop.campaign || drop.title} (${normalizeCode(drop.code)})`
      });
      return;
    }
    // 3) Nothing matched.
    setScanVerdict({
      kind: 'invalid',
      title: 'Invalid code',
      detail: `"${code}" doesn't match any redemption code or drop.`
    });
  };
  // --- EXPORTS ---
  const exportUsers = () => {
    const headers = 'Name,Phone,Age,City,Occupation,Interests,Pickup Preference,SMS Consent,Catch Count,New vs Returning,Points,Level,Eligible,Registered At,Last Seen At';
    const rows = sheetUsers.map(u => {
      const catchCount = u.catchCount || 1;
      const segment = catchCount >= 2 ? 'Returning' : 'New';
      const eligible = sheetCatches.some(c => c.userId === u.phone && c.status === 'reviewed');
      return `"${u.name}","${u.phone}","${u.ageRange || ''}","${u.city || ''}","${u.occupation || ''}","${(u.interests || []).join('; ')}","${u.storePreference || ''}","Yes",${catchCount},"${segment}",${u.points},"${u.level}",${eligible},"${new Date(u.registeredAt).toLocaleString()}","${u.lastSeenAt ? new Date(u.lastSeenAt).toLocaleString() : ''}"`;
    }).join('\n');
    downloadCSV('finns-i-sjon-users.csv', headers + '\n' + rows);
  };
  const exportReservations = () => {
    const headers = 'ID,User,Event,Brand,Batch Code,Round,Status,User Code,Staff Code,Recurring,Created At';
    const rows = sheetCatches.map(r => {
      return `"${r.id}","${r.userId}","${r.eventTitle || ''}","${r.eventBrand || ''}","${r.batchCode || ''}","${r.round}","${r.status}","${r.userCode}","${r.staffCode}","${r.recurring ? 'Yes' : ''}","${new Date(r.timestamp).toLocaleString()}"`;
    }).join('\n');
    downloadCSV('finns-i-sjon-reservations.csv', headers + '\n' + rows);
  };
  const exportReviews = () => {
    const headers = 'Review ID,Reservation ID,User,Product,Brand,Taste,Packaging,Buy Again,Date';
    const rows = sheetReviews.map(r => `"${r.id}","${r.reservationId}","${r.userId}","${r.productName}","${r.productBrand}",${r.taste === null ? '' : r.taste},${r.packaging === null ? '' : r.packaging},${r.buyAgain === null ? '' : r.buyAgain},"${new Date(r.timestamp).toLocaleString()}"`).join('\n');
    downloadCSV('finns-i-sjon-reviews.csv', headers + '\n' + rows);
  };
  // Joined behavior export: every review enriched with the reviewer's profile,
  // so reviews ⇄ products ⇄ occupation/taste can be analysed in one sheet.
  const exportJoinedBehavior = (joined: JoinedReviewRow[]) => {
    const headers = 'Review ID,Phone,Name,Occupation,Age,City,Interests,Product Brand,Product,Taste,Packaging,Buy Again';
    const rows = joined.map(r => [csvCell(r.reviewId), csvCell(r.phone), csvCell(r.name), csvCell(r.occupation), csvCell(r.ageRange), csvCell(r.city), csvCell(r.interests.join('; ')), csvCell(r.productBrand), csvCell(r.productName), csvCell(r.taste === null ? '' : r.taste ? 'Yes' : 'No'), csvCell(r.packaging === null ? '' : r.packaging ? 'Yes' : 'No'), csvCell(r.buyAgain === null ? '' : r.buyAgain ? 'Yes' : 'No')].join(',')).join('\n');
    downloadCSV('finns-i-sjon-behavior.csv', headers + '\n' + rows);
  };
  const exportDrops = () => {
    const headers = 'Drop Code,Drop ID,Title,Brand,Description,Date,Time,Address,Theme,Type,Required Points,Total Boxes,Boxes Left,Batch/Round,Active';
    const rows = events.map(e => `"${e.code || ''}","${e.id}","${e.title}","${e.brand}","${e.description}","${e.date}","${e.time}","${e.address}","${e.theme}","${e.type}",${e.requiredPoints},${e.totalBoxes},${e.boxesLeft},"R${activeRound} ${activeBatchCode}",${e.id === activeEventId}`).join('\n');
    downloadCSV('finns-i-sjon-drops.csv', headers + '\n' + rows);
  };
  // --- FULL MIGRATION EXPORT ---
  // A complete, self-contained snapshot of every record so the whole dataset
  // can be re-imported into a new app without losing anything.
  const stamp = () => new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const exportFullBackup = () => {
    const enrichedUsers = sheetUsers.map(u => {
      const phoneKey = u.phone.replace(/\s/g, '');
      return {
        ...u,
        eligible_for_next_event: sheetCatches.some(c => c.userId === u.phone && c.status === 'reviewed'),
        invitesSent: sheetUsers.filter(other => other.invitedBy === phoneKey).length
      };
    });
    const backup = {
      app: 'finns-i-sjon',
      schemaVersion: 5,
      exportedAt: new Date().toISOString(),
      counts: {
        users: enrichedUsers.length,
        catches: sheetCatches.length,
        reviews: sheetReviews.length,
        drops: events.length
      },
      users: enrichedUsers,
      catches: sheetCatches,
      reviews: sheetReviews,
      drops: events,
      recurringQuestions,
      partnerCodes
    };
    downloadJSON(`finns-i-sjon-full-backup-${stamp()}.json`, backup);
  };
  // Every user field as one wide CSV row — the canonical "move my users" file.
  const exportCompleteUsers = () => {
    const headers = ['Name', 'Phone', 'Age', 'City', 'Occupation', 'Profession', 'Work Area', 'Favorite Area', 'Interests', 'Pickup Preference', 'Custom Answers', 'Invited By', 'Partner Code', 'Invites Sent', 'Catch Count', 'New vs Returning', 'Points', 'Level', 'Eligible For Next', 'Registered At', 'Last Seen At'].join(',');
    const rows = sheetUsers.map(u => {
      const phoneKey = u.phone.replace(/\s/g, '');
      const catchCount = u.catchCount || 1;
      const segment = catchCount >= 2 ? 'Returning' : 'New';
      const eligible = sheetCatches.some(c => c.userId === u.phone && c.status === 'reviewed');
      const invitesSent = sheetUsers.filter(other => other.invitedBy === phoneKey).length;
      return [csvCell(u.name), csvCell(u.phone), csvCell(u.ageRange), csvCell(u.city), csvCell(u.occupation), csvCell(u.profession), csvCell(u.workArea), csvCell(u.favoriteArea), csvCell((u.interests || []).join('; ')), csvCell(u.storePreference), csvCell(JSON.stringify(u.customAnswers ?? {})), csvCell(u.invitedBy), csvCell(u.partnerCode), invitesSent, catchCount, csvCell(segment), u.points, csvCell(u.level), eligible, csvCell(u.registeredAt ? new Date(u.registeredAt).toLocaleString() : ''), csvCell(u.lastSeenAt ? new Date(u.lastSeenAt).toLocaleString() : '')].join(',');
    }).join('\n');
    downloadCSV(`finns-i-sjon-complete-users-${stamp()}.csv`, headers + '\n' + rows);
  };
  // --- STATS ---
  const totalUsers = sheetUsers.length;
  const totalReservations = sheetCatches.length;
  const totalRedeemed = sheetCatches.filter(r => r.status === 'redeemed' || r.status === 'reviewed').length;
  const totalReviewed = sheetCatches.filter(r => r.status === 'reviewed').length;
  const reviewRate = totalRedeemed > 0 ? Math.round(totalReviewed / totalRedeemed * 100) : 0;
  // The sheet has no eligibility flag, so derive it: a user is "eligible for
  // next" once they have at least one reviewed catch.
  const eligibleUsers = new Set(sheetCatches.filter(c => c.status === 'reviewed').map(c => c.userId)).size;
  // New vs returning counts — catchCount is the canonical source of truth
  const returningUsers = sheetUsers.filter(u => (u.catchCount || 1) >= 2).length;
  const newUsers = sheetUsers.filter(u => (u.catchCount || 1) === 1).length;
  const tabs = [{
    id: 'stats',
    label: 'Dashboard',
    icon: BarChart3
  }, {
    id: 'demographics',
    label: 'Demographics',
    icon: PieChart
  }, {
    id: 'verify',
    label: 'Verify',
    icon: ShieldCheck
  }, {
    id: 'users',
    label: 'Users',
    icon: Users
  }, {
    id: 'referrals',
    label: 'Referrals',
    icon: Users
  }, {
    id: 'reviews',
    label: 'Reviews',
    icon: ClipboardList
  }, {
    id: 'insights',
    label: 'Insights',
    icon: Brain
  }, {
    id: 'drops',
    label: 'Drops',
    icon: Package
  }, {
    id: 'tools',
    label: 'Tools',
    icon: DatabaseBackup
  }];
  const toggleUserExpanded = (phone: string) => {
    if (expandedUserPhone === phone) {
      setExpandedUserPhone(null);
    } else {
      setExpandedUserPhone(phone);
    }
  };
  // Prefill the create-drop form from a Smart Drop Engine recognition result,
  // then open the form so the admin can review, fill availability + address,
  // and save.
  const applyRecognition = (r: RecognitionResult) => {
    setNewDrop(d => ({
      ...d,
      title: r.title,
      brand: r.brand,
      campaign: r.campaign,
      description: r.description,
      brandLogo: r.brandLogo,
      instagramUrl: r.instagramUrl,
      websiteUrl: r.websiteUrl
    }));
    setNewDropProducts(r.products);
    setNewDropQuestions(r.questions);
    setNewDropAvailability(DEFAULT_AVAILABILITY);
    setAddDropError(null);
    setShowAddDrop(true);
    // Bring the freshly-prefilled form into view.
    window.setTimeout(() => {
      document.getElementById('add-drop-form')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 80);
  };
  const handleAddDrop = (e: React.FormEvent) => {
    e.preventDefault();
    // Explicit validation so saving never silently fails: only Title, Brand and
    // a positive Amount are truly required. Everything else has a sensible
    // fallback (campaign → title, availability → rolling, logo → derived).
    const missing: string[] = [];
    if (!newDrop.title.trim()) missing.push('Product Title');
    if (!newDrop.brand.trim()) missing.push('Brand');
    if (!(Number(newDrop.totalBoxes) > 0)) missing.push('Amount (Total Boxes)');
    if (missing.length > 0) {
      setAddDropError(`Please fill in: ${missing.join(', ')}.`);
      return;
    }
    setAddDropError(null);
    // Auto-derive the brand logo when the admin didn't paste one, so users
    // immediately recognise what they're reviewing.
    const brandLogo = newDrop.brandLogo.trim() || (newDrop.brand.trim() ? brandMeta(newDrop.brand.trim()).brandLogo : '');
    // Give each product a logo (its own brand's, falling back to the drop's).
    const productsWithLogos: DropProduct[] = newDropProducts.map(p => ({
      ...p,
      logo: p.logo || (p.brand?.trim() ? brandMeta(p.brand.trim()).brandLogo : brandLogo || undefined)
    }));
    // Map the selected required level → its point threshold (kept in
    // requiredPoints so existing gating keeps working).
    const requiredPoints = pointsForLevel(newDrop.requiredLevel);
    const targeting: DropTargeting = {
      ...newDropTargeting,
      minLevel: newDrop.requiredLevel && requiredPoints > 0 ? newDrop.requiredLevel : undefined
    };
    // Resolve the availability model into concrete date/time/recurrence/enabled.
    const availability = resolveAvailability(newDropAvailability);
    addEvent({
      id: Math.random().toString(36).substr(2, 9),
      code: generateDropCode(),
      title: newDrop.title,
      brand: newDrop.brand,
      campaign: newDrop.campaign.trim() || newDrop.title,
      verificationMethod: newDrop.verificationMethod,
      description: newDrop.description,
      date: availability.date,
      time: availability.time,
      address: newDrop.address,
      totalBoxes: Number(newDrop.totalBoxes),
      boxesLeft: Number(newDrop.totalBoxes),
      theme: 'New Drop',
      type: 'reservation',
      recurrence: availability.recurrence,
      enabled: availability.enabled,
      requiredPoints,
      targeting,
      brandLogo,
      instagramUrl: newDrop.instagramUrl,
      websiteUrl: newDrop.websiteUrl,
      customQuestions: newDropQuestions,
      products: productsWithLogos
    });
    setShowAddDrop(false);
    setNewDrop({
      title: '',
      brand: '',
      campaign: '',
      verificationMethod: 'both',
      description: '',
      address: '',
      totalBoxes: 100,
      requiredLevel: LEVEL_NAMES[0],
      brandLogo: '',
      instagramUrl: '',
      websiteUrl: ''
    });
    setNewDropTargeting({});
    setNewDropAvailability(DEFAULT_AVAILABILITY);
    setNewDropQuestions([]);
    setNewDropProducts([]);
  };
  // --- SEED / TEST DATA ---
  const logSeed = (line: string) => setSeedLog(prev => [`${new Date().toLocaleTimeString()}  ${line}`, ...prev]);
  // Create the configured campaigns/brands/drops.
  const seedDrops = () => {
    const drops = buildMockDrops(dropConfig);
    drops.forEach(d => addEvent(d)); // addEvent syncs each to the sheet
    logSeed(`Created ${drops.length} drops across ${dropConfig.campaigns} campaign(s) × ${dropConfig.brandsPerCampaign} brand(s), ${dropConfig.boxesPerDrop} boxes each.`);
    return drops;
  };
  // Generate the full mock dataset: drops + users + catches + reviews.
  const seedFullDataset = async () => {
    if (isSeeding) return;
    setIsSeeding(true);
    setSeedLog([]);
    setSeedProgress({
      done: 0,
      total: 0
    });
    try {
      // Reuse existing drops if present, otherwise create the configured drops
      // so catches reference real drops.
      const drops = events.length > 0 ? events : seedDrops();
      logSeed(`Building ${userConfig.count} users + catches + reviews…`);
      const batch = buildMockUsers(drops, userConfig);
      logSeed(`Prepared ${batch.users.length} users, ${batch.catches.length} catches, ${batch.reviews.length} reviews.`);
      const result = await bulkSeed(batch, (done, total) => setSeedProgress({
        done,
        total
      }));
      logSeed(result.ok ? `✓ Wrote ${result.written} rows to the sheet.` : `⚠ Wrote ${result.written} rows, but some chunks failed — check console / retry.`);
      await loadSheetData();
      logSeed('Dashboard refreshed with the new data.');
    } catch (err) {
      logSeed(`✗ Seeding failed: ${String(err)}`);
    } finally {
      setIsSeeding(false);
    }
  };
  // Adjust a drop's amount (total boxes) live; boxesLeft moves by the same delta.
  const adjustAmount = (id: string, delta: number) => {
    const ev = events.find(e => e.id === id);
    if (!ev) return;
    const nextTotal = Math.max(0, ev.totalBoxes + delta);
    const nextLeft = Math.max(0, ev.boxesLeft + delta);
    updateEvent(id, {
      totalBoxes: nextTotal,
      boxesLeft: nextLeft
    });
  };
  // Restock a drop: add the entered amount to BOTH total + remaining boxes
  // (redeemed boxes untouched), then clear the input and flash "Saved".
  const handleRefill = (id: string) => {
    const amount = Math.max(0, Math.round(refillAmounts[id] || 0));
    if (amount === 0) return;
    refillDrop(id, amount);
    setRefillAmounts(prev => ({
      ...prev,
      [id]: 0
    }));
    flashSaved(id);
  };
  const handleDeleteDrop = (id: string, title: string) => {
    if (window.confirm(`Delete drop "${title}"? This can't be undone.`)) {
      deleteEvent(id);
    }
  };
  // Brand logged in → render their focused, brand-only data view (nothing else).
  if (loggedInBrand) {
    return <BrandDashboard brand={loggedInBrand} events={events} catches={sheetCatches} reviews={sheetReviews} users={sheetUsers} questionLabelMap={questionLabelMap} onLogout={() => {
      setLoggedInBrand(null);
      setBrandName('');
      setBrandPassword('');
    }} data-id="element-865" data-mp="16kwc7baxjd00" />;
  }
  if (!isAuthenticated) {
    return <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center p-4" data-id="element-866" data-mp="nmaem6emg40u">
        {/* Big logo above the login card — mirrors the consumer landing page */}
        <div className="w-72 sm:w-80 max-w-full mb-8" data-id="element-867" data-mp="8vb9j5czvxm0">
          <img src="https://cdn.magicpatterns.com/uploads/bcWTzmedNUNKBwzbD46o8N/2.png" alt="GONE FISHING" className="w-full h-auto object-contain" data-id="element-868" data-mp="dxin1zdh3lqg" />
        </div>
        <div className="bg-gray-900 border border-gray-800 p-8 rounded-2xl w-full max-w-sm" data-id="element-869" data-mp="4uhkg8ntgcrj">
          <div className="flex items-center justify-center mb-6" data-id="element-870" data-mp="3hg6fiwyxrbly">
            <span className="text-xs bg-brand-yellow/20 text-brand-yellow px-3 py-1.5 rounded-full font-mono uppercase tracking-wider" data-id="element-871" data-mp="3cgk5eweembhd">
              Admin
            </span>
          </div>
          <form onSubmit={e => {
          e.preventDefault();
          if (password === authConfig.adminPassword) setIsAuthenticated(true);else setLoginError('Wrong password.');
        }} data-id="element-872" data-mp="3mehsvhofw48x">
            <Input type="password" placeholder="Enter admin password" value={password} onChange={e => setPassword(e.target.value)} className="bg-gray-800 border-gray-700 text-white mb-4 text-center" autoFocus data-id="element-873" data-mp="3vemufb6yc8f6" />
            {loginError && <p className="text-red-400 text-xs mb-3 text-center" data-id="element-874" data-mp="i09qeridpd3n">
                {loginError}
              </p>}
            <Button type="submit" className="w-full" data-id="element-875" data-mp="2ch9s8f3lgrmn">
              Login
            </Button>
          </form>
          {/* Brand login now lives on its own page at /brand. */}
          <div className="mt-6 pt-5 border-t border-gray-800 text-center" data-id="element-876" data-mp="2m6jpe9dap0hm">
            <p className="text-xs text-gray-500 mb-2" data-id="element-877" data-mp="2m8nqkjjt7le7">
              Are you a brand looking for your data?
            </p>
            <a href="/brand" className="inline-block text-sm font-bold text-brand-blue hover:text-brand-blue/80 transition-colors" data-id="element-878" data-mp="1thvzg0hlpmxq">
              Go to the Brand Portal →
            </a>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-950 text-white" data-id="element-879" data-mp="27hdrcoq71gbb">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between sticky top-0 z-10" data-id="element-880" data-mp="27faqtarf04sc">
        <div className="flex items-center space-x-3" data-id="element-881" data-mp="3iwaj46drijmd">
          <div className="bg-white rounded-lg px-2.5 py-1 flex items-center" data-id="element-882" data-mp="3iupmmb7w0qlv">
            <img src="https://cdn.magicpatterns.com/uploads/v1hkrrPyRZcojtV9rXN8u6/2.png" alt="GONE FISHING" className="h-8 object-contain" data-id="element-883" data-mp="3sz61b19ms1o3" />
          </div>
          <span className="text-xs bg-brand-yellow/20 text-brand-yellow px-2 py-0.5 rounded font-mono" data-id="element-884" data-mp="3j94nd2azrzmw">
            ADMIN
          </span>
        </div>
        <div className="flex items-center space-x-4" data-id="element-885" data-mp="3a9q1kg5rsd8q">
          {lastRefreshed && <span className="text-xs text-gray-500" data-id="element-886" data-mp="35awlqh30ga5e">
              Updated {lastRefreshed.toLocaleTimeString()}
            </span>}
          <button onClick={loadSheetData} disabled={isLoadingSheet} className="flex items-center text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-1.5 rounded-lg transition-colors" data-id="element-887" data-mp="30cv0pqm0y0ra">
            <RefreshCw className={`w-3 h-3 mr-1.5 ${isLoadingSheet ? 'animate-spin text-blue-400' : ''}`} data-id="element-888" data-mp="yici0igvodet" />
            {isLoadingSheet ? 'Syncing...' : 'Refresh'}
          </button>
          <div className="bg-green-500 px-3 py-1 rounded-full text-xs font-bold text-black" data-id="element-889" data-mp="2nzrad1pas16u">
            Live
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-gray-900 border-b border-gray-800 px-4 flex space-x-1 overflow-x-auto" data-id="element-890" data-mp="2f2qrfakzyvas">
        {tabs.map(t => <button key={t.id} onClick={() => setTab(t.id as Tab)} className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t.id ? 'border-brand-blue text-brand-blue' : 'border-transparent text-gray-500 hover:text-gray-300'}`} data-id="element-891" data-mp="2j051t5kez94d">
            <t.icon className="w-4 h-4" data-id="element-892" data-mp="32pff6cwothzb" />
            <span data-id="element-893" data-mp="32sl2bmlra9se">{t.label}</span>
          </button>)}
      </div>

      <div className="p-6" data-id="element-894" data-mp="1mankftc2jymu">
        {/* STATS TAB */}
        {tab === 'stats' && <div className="space-y-6" data-id="element-895" data-mp="1rc35hle8y558">
            <div className="grid grid-cols-2 gap-4" data-id="element-896" data-mp="1r8yti1uji540">
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-id="element-897" data-mp="1it9n9zn17xqc">
                <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-898" data-mp="1et8zvphygc75">
                  Total Users
                </p>
                <p className="text-3xl font-black text-white mt-1" data-id="element-899" data-mp="1sr5skatafygj">
                  {totalUsers}
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-id="element-900" data-mp="1nrksty6anm2r">
                <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-901" data-mp="101ql1v0u0npd">
                  Reservations
                </p>
                <p className="text-3xl font-black text-white mt-1" data-id="element-902" data-mp="19tcsrhdyo4nd">
                  {totalReservations}
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-id="element-903" data-mp="1524kioodapbs">
                <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-904" data-mp="hca6i3sbaupy">
                  Returning Users
                </p>
                <p className="text-3xl font-black text-purple-400 mt-1" data-id="element-905" data-mp="cdyy7xho0s9u">
                  {returningUsers}
                </p>
                <p className="text-[10px] text-gray-500 mt-1" data-id="element-906" data-mp="3rzeon4vsin4d">
                  2+ catches (came back)
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-id="element-907" data-mp="sah88vqgyvf">
                <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-908" data-mp="5sgbzxxav1hq">
                  New Users
                </p>
                <p className="text-3xl font-black text-white mt-1" data-id="element-909" data-mp="9t8msvltleg4">
                  {newUsers}
                </p>
                <p className="text-[10px] text-gray-500 mt-1" data-id="element-910" data-mp="eqs0nuelvp7j">
                  First catch only
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-id="element-911" data-mp="oiehc69pqgho">
                <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-912" data-mp="thr6wyryy1ny">
                  Redeemed
                </p>
                <p className="text-3xl font-black text-white mt-1" data-id="element-913" data-mp="xiui37z2n9iu">
                  {totalRedeemed}
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-id="element-914" data-mp="2i2pzb063b0n5">
                <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-915" data-mp="2dz0dujkpoz6c">
                  Review Rate
                </p>
                <p className="text-3xl font-black text-white mt-1" data-id="element-916" data-mp="292t5kk4ufbl8">
                  {reviewRate}%
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-id="element-917" data-mp="31pvy8vmu8s3k">
                <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-918" data-mp="2wqb4czmiz9xp">
                  Eligible for Next
                </p>
                <p className="text-3xl font-black text-green-400 mt-1" data-id="element-919" data-mp="2ryj94bsfcg18">
                  {eligibleUsers}
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-id="element-920" data-mp="2mynf95dfy4ze">
                <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-921" data-mp="3kh6qbg4avax1">
                  Total Reviews
                </p>
                <p className="text-3xl font-black text-white mt-1" data-id="element-922" data-mp="16zvzm2havv8">
                  {sheetReviews.length}
                </p>
              </div>
            </div>

            {/* Campaign Performance & Conversion Funnel */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-923" data-mp="3ndg0oz4y2eu4">
              <div className="bg-gray-900 rounded-xl p-6 border border-gray-800" data-id="element-924" data-mp="3jcmcs5ri5018">
                <h3 className="font-bold mb-4 flex items-center text-white" data-id="element-925" data-mp="3jep895bfbldc">
                  <BarChart3 className="w-5 h-5 mr-2 text-brand-yellow" data-id="element-926" data-mp="jwzic16xpwuy" />
                  Conversion Funnel
                </h3>
                <div className="space-y-4" data-id="element-927" data-mp="f0qxc76239fv">
                  <div data-id="element-928" data-mp="a6mnng5eonb3">
                    <div className="flex justify-between text-sm mb-1" data-id="element-929" data-mp="a5v1sx1m23y0">
                      <span className="text-gray-400" data-id="element-930" data-mp="651fg83tgx78">Total Caught</span>
                      <span className="font-bold text-white" data-id="element-931" data-mp="12nqoktvml754">
                        {totalReservations}
                      </span>
                    </div>
                    <div className="w-full bg-gray-800 h-2 rounded-full overflow-hidden" data-id="element-932" data-mp="xvz1itbohjjq">
                      <div className="bg-blue-500 h-full" style={{
                    width: '100%'
                  }} data-id="element-933" data-mp="2glux43y8pm1o"></div>
                    </div>
                  </div>
                  <div data-id="element-934" data-mp="2uk1n6xwy6hqs">
                    <div className="flex justify-between text-sm mb-1" data-id="element-935" data-mp="2ukkxptk9q3us">
                      <span className="text-gray-400" data-id="element-936" data-mp="2zjo1zbsfc61p">Redeemed</span>
                      <span className="font-bold text-white" data-id="element-937" data-mp="34hzbtmipygjx">
                        {totalRedeemed} (
                        {totalReservations > 0 ? Math.round(totalRedeemed / totalReservations * 100) : 0}
                        %)
                      </span>
                    </div>
                    <div className="w-full bg-gray-800 h-2 rounded-full overflow-hidden" data-id="element-938" data-mp="3n8grapejjfnc">
                      <div className="bg-brand-yellow h-full" style={{
                    width: `${totalReservations > 0 ? totalRedeemed / totalReservations * 100 : 0}%`
                  }} data-id="element-939" data-mp="1n67deqohisz4"></div>
                    </div>
                  </div>
                  <div data-id="element-940" data-mp="1s50qwj3rujza">
                    <div className="flex justify-between text-sm mb-1" data-id="element-941" data-mp="1s3ppur1pnqbv">
                      <span className="text-gray-400" data-id="element-942" data-mp="25vxic6lyov4r">Reviewed</span>
                      <span className="font-bold text-white" data-id="element-943" data-mp="21v3ufd8irgbv">
                        {totalReviewed} (
                        {totalRedeemed > 0 ? Math.round(totalReviewed / totalRedeemed * 100) : 0}
                        %)
                      </span>
                    </div>
                    <div className="w-full bg-gray-800 h-2 rounded-full overflow-hidden" data-id="element-944" data-mp="lhwwyr1lnu0y">
                      <div className="bg-green-500 h-full" style={{
                    width: `${totalRedeemed > 0 ? totalReviewed / totalRedeemed * 100 : 0}%`
                  }} data-id="element-945" data-mp="gikc2buagx10"></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-xl p-6 border border-gray-800" data-id="element-946" data-mp="2idglq74ghodu">
                <h3 className="font-bold mb-4 flex items-center text-white" data-id="element-947" data-mp="2ncb63di2nl02">
                  <Star className="w-5 h-5 mr-2 text-brand-yellow" data-id="element-948" data-mp="29d1qaexs2swm" />
                  Campaign Performance
                </h3>
                <div className="space-y-4" data-id="element-949" data-mp="2ebczqok5v1eb">
                  {events.slice(0, 3).map(e => {
                const dropCatches = sheetCatches.filter(c => c.eventTitle === e.title);
                const dropRedeemed = dropCatches.filter(c => c.status === 'redeemed' || c.status === 'reviewed').length;
                const dropReviewed = dropCatches.filter(c => c.status === 'reviewed').length;
                return <div key={e.id} className="border-b border-gray-800 pb-3 last:border-0 last:pb-0" data-id="element-950" data-mp="1uqhnr7xpcbk9">
                        <div className="flex justify-between items-center mb-1" data-id="element-951" data-mp="1kqqsv558bujn">
                          <span className="font-bold text-sm text-white" data-id="element-952" data-mp="1fspa6lbptx8p">
                            {e.title}
                          </span>
                          <span className="text-xs text-gray-400" data-id="element-953" data-mp="1azmfg9v7chqx">
                            {e.brand}
                          </span>
                        </div>
                        <div className="flex space-x-4 text-xs" data-id="element-954" data-mp="1223tn39qhzdz">
                          <span className="text-gray-400" data-id="element-955" data-mp="x1ody51zcfsu">
                            Caught:{' '}
                            <span className="text-white" data-id="element-956" data-mp="s34o3tk9mjv7">
                              {dropCatches.length}
                            </span>
                          </span>
                          <span className="text-gray-400" data-id="element-957" data-mp="i1ahd49brqbq">
                            Redeemed:{' '}
                            <span className="text-white" data-id="element-958" data-mp="mzu436vqt655">{dropRedeemed}</span>
                          </span>
                          <span className="text-gray-400" data-id="element-959" data-mp="wvx7eg2bzato">
                            Reviewed:{' '}
                            <span className="text-white" data-id="element-960" data-mp="3ve2uc8aslku5">{dropReviewed}</span>
                          </span>
                        </div>
                      </div>;
              })}
                  {events.length === 0 && <p className="text-sm text-gray-500" data-id="element-961" data-mp="951mm2walfvx">
                      No active campaigns.
                    </p>}
                </div>
              </div>
            </div>

            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800" data-id="element-962" data-mp="3cn2xkkx236qb">
              <h3 className="font-bold mb-4 flex items-center" data-id="element-963" data-mp="3hl4rk0w0sqgk">
                <Download className="w-5 h-5 mr-2 text-brand-blue" data-id="element-964" data-mp="39lgifvspwl2e" />
                Export Data
              </h3>
              <div className="space-y-3" data-id="element-965" data-mp="34nf1phirkxu3">
                <button onClick={exportUsers} className="w-full flex items-center justify-between bg-gray-800 hover:bg-gray-700 p-4 rounded-lg transition-colors" data-id="element-966" data-mp="34mdi2onon30r">
                  <div className="flex items-center" data-id="element-967" data-mp="3eesz9jr5nnkl">
                    <FileSpreadsheet className="w-5 h-5 text-green-400 mr-3" data-id="element-968" data-mp="2qnnh2xxqsj74" />
                    <div className="text-left" data-id="element-969" data-mp="2lokoicyy85g1">
                      <p className="font-medium" data-id="element-970" data-mp="2lpvrwbohfb6m">Users Data</p>
                      <p className="text-xs text-gray-500" data-id="element-971" data-mp="30m37szsuxqg3">
                        {totalUsers} records • Name, Phone, Age, City,
                        Interests, Pickup
                      </p>
                    </div>
                  </div>
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded font-mono" data-id="element-972" data-mp="fwnpkt7pfa22">
                    .CSV
                  </span>
                </button>
                <button onClick={exportReservations} className="w-full flex items-center justify-between bg-gray-800 hover:bg-gray-700 p-4 rounded-lg transition-colors" data-id="element-973" data-mp="idxrp1hcnrow">
                  <div className="flex items-center" data-id="element-974" data-mp="9gx612lo07p5">
                    <FileSpreadsheet className="w-5 h-5 text-blue-400 mr-3" data-id="element-975" data-mp="dgxnyx7z310y" />
                    <div className="text-left" data-id="element-976" data-mp="115gtj4zn0zpc">
                      <p className="font-medium" data-id="element-977" data-mp="117tm0c30usgn">Reservations</p>
                      <p className="text-xs text-gray-500" data-id="element-978" data-mp="163s79qt1zucu">
                        {totalReservations} records • Event, Status, Codes,
                        Timestamps
                      </p>
                    </div>
                  </div>
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded font-mono" data-id="element-979" data-mp="wcnrwpzgu7hh">
                    .CSV
                  </span>
                </button>
                <button onClick={exportReviews} className="w-full flex items-center justify-between bg-gray-800 hover:bg-gray-700 p-4 rounded-lg transition-colors" data-id="element-980" data-mp="1owm8zj1o1pz4">
                  <div className="flex items-center" data-id="element-981" data-mp="vc40dcrpzehx">
                    <FileSpreadsheet className="w-5 h-5 text-purple-400 mr-3" data-id="element-982" data-mp="qfx0o1px2z8f" />
                    <div className="text-left" data-id="element-983" data-mp="lgantte03l05">
                      <p className="font-medium" data-id="element-984" data-mp="lhlqfpvpmmpo">Product Reviews</p>
                      <p className="text-xs text-gray-500" data-id="element-985" data-mp="1j2hr8zykj7ax">
                        {sheetReviews.length} records • Product, Taste,
                        Packaging, Buy Again
                      </p>
                    </div>
                  </div>
                  <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded font-mono" data-id="element-986" data-mp="146abcbu70s1g">
                    .CSV
                  </span>
                </button>
                <button onClick={exportDrops} className="w-full flex items-center justify-between bg-gray-800 hover:bg-gray-700 p-4 rounded-lg transition-colors" data-id="element-987" data-mp="3tthntj3gr6mu">
                  <div className="flex items-center" data-id="element-988" data-mp="24cm75yu3pb0e">
                    <FileSpreadsheet className="w-5 h-5 text-orange-400 mr-3" data-id="element-989" data-mp="29ebjriwz1wql" />
                    <div className="text-left" data-id="element-990" data-mp="2eccxrpfjj5v7">
                      <p className="font-medium" data-id="element-991" data-mp="2eajsadgnyros">Drops / Products</p>
                      <p className="text-xs text-gray-500" data-id="element-992" data-mp="2j44s8b1thvqm">
                        {events.length} records • Title, Brand, Amount, Boxes
                        Left, Date
                      </p>
                    </div>
                  </div>
                  <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-1 rounded font-mono" data-id="element-993" data-mp="2x34g3ghia6lq">
                    .CSV
                  </span>
                </button>
              </div>
            </div>

            {/* Full migration / transfer export */}
            <div className="bg-gray-900 rounded-xl p-6 border border-brand-yellow/30" data-id="element-994" data-mp="12qxs1h3ijvc0">
              <h3 className="font-bold mb-1 flex items-center" data-id="element-995" data-mp="17opvn8dit5w1">
                <DatabaseBackup className="w-5 h-5 mr-2 text-brand-yellow" data-id="element-996" data-mp="t9pypefu86vp" />
                Transfer / Migrate Data
              </h3>
              <p className="text-xs text-gray-500 mb-4" data-id="element-997" data-mp="y29b482sgvyx">
                Download a complete, portable copy of everything — every user
                profile with all collected fields, plus catches, reviews and
                drops — so you can move the whole dataset into a new app at any
                time.
              </p>
              <div className="space-y-3" data-id="element-998" data-mp="1bymq9ajuola8">
                <button onClick={exportFullBackup} className="w-full flex items-center justify-between bg-brand-yellow/10 hover:bg-brand-yellow/20 border border-brand-yellow/30 p-4 rounded-lg transition-colors" data-id="element-999" data-mp="1gyixdinugsu0">
                  <div className="flex items-center" data-id="element-1000" data-mp="3mssdhdd0vujz">
                    <FileJson className="w-5 h-5 text-brand-yellow mr-3" data-id="element-1001" data-mp="3mqz77z6bncci" />
                    <div className="text-left" data-id="element-1002" data-mp="3rsohhclpzlzj">
                      <p className="font-medium text-white" data-id="element-1003" data-mp="3rp91m7k828x2">
                        Full Backup (everything)
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-1004" data-mp="24vfbz0n6zlsx">
                        {totalUsers} users • {totalReservations} catches •{' '}
                        {sheetReviews.length} reviews • {events.length} drops —
                        one importable file
                      </p>
                    </div>
                  </div>
                  <span className="text-xs bg-brand-yellow/20 text-brand-yellow px-2 py-1 rounded font-mono" data-id="element-1005" data-mp="1r40s6rlgngkt">
                    .JSON
                  </span>
                </button>
                <button onClick={exportCompleteUsers} className="w-full flex items-center justify-between bg-gray-800 hover:bg-gray-700 p-4 rounded-lg transition-colors" data-id="element-1006" data-mp="1zwm4boul6p0o">
                  <div className="flex items-center" data-id="element-1007" data-mp="17df6d74srbsf">
                    <Users className="w-5 h-5 text-green-400 mr-3" data-id="element-1008" data-mp="13epvpmb588ch" />
                    <div className="text-left" data-id="element-1009" data-mp="32io9wiqz09jq">
                      <p className="font-medium text-white" data-id="element-1010" data-mp="32hwr68ih26av">
                        Complete Users (all fields)
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-1011" data-mp="37feqd76ot75o">
                        {totalUsers} records • every profile field, referrals &
                        custom answers
                      </p>
                    </div>
                  </div>
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded font-mono" data-id="element-1012" data-mp="2jl3x4a1hd1fh">
                    .CSV
                  </span>
                </button>
              </div>
              <p className="text-[11px] text-gray-600 mt-4 leading-relaxed" data-id="element-1013" data-mp="2tgyn5q819ysa">
                Tip: the JSON file is the most complete — it preserves the exact
                structure of every record (including referral links and per-drop
                questions) so a new app can rebuild the full dataset.
              </p>
            </div>

            {/* Supabase connection test */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800" data-id="element-1014" data-mp="1y965hhgh9nb5">
              <h3 className="font-bold mb-2 flex items-center" data-id="element-1015" data-mp="1t99xl74ntbqb">
                <RefreshCw className="w-5 h-5 mr-2 text-blue-400" data-id="element-1016" data-mp="1tbwehxdfx3l7" />
                Supabase Connection
              </h3>
              <p className="text-xs text-gray-500 mb-4" data-id="element-1017" data-mp="1jk9wni6lk69h">
                Reads the Supabase database and reports whether it is reachable.
                Use this to confirm the backend is connected. It does not write
                any test rows.
              </p>
              <Button onClick={runConnectionTest} isLoading={isTesting} className="w-full" data-id="element-1018" data-mp="1ai0q13eg8x1d">
                Test Supabase connection
              </Button>
              {testResult && <div className={`mt-4 rounded-lg p-4 border text-sm ${testResult.ok ? 'bg-green-900/30 border-green-500/50' : 'bg-red-900/30 border-red-500/50'}`} data-id="element-1019" data-mp="2zyi7wglx2t5t">
                  <p className={`font-bold mb-1 ${testResult.ok ? 'text-green-400' : 'text-red-400'}`} data-id="element-1020" data-mp="1slhgvgmej0iz">
                    {testResult.ok ? `✓ Success — Supabase is reachable.` : `✗ Failed (${testResult.status || 'no response'})`}
                  </p>
                  <pre className="text-xs text-gray-400 whitespace-pre-wrap break-all mt-2" data-id="element-1021" data-mp="1nktg438df5bk">
                    {testResult.body || '(empty response)'}
                  </pre>
                  {!testResult.ok && <p className="text-xs text-gray-500 mt-3" data-id="element-1022" data-mp="1dqkyiakvows6">
                      If this fails, the most likely cause is that the database
                      tables haven't been created yet — run the setup SQL from
                      lib/supabase.ts in your Supabase SQL editor — or that Row
                      Level Security is blocking the anon key.
                    </p>}
                </div>}
            </div>
          </div>}

        {/* DEMOGRAPHICS TAB */}
        {tab === 'demographics' && <div className="space-y-6" data-id="element-1023" data-mp="7bn6th1ukdf0">
            <div className="flex justify-between items-center" data-id="element-1024" data-mp="7coinnqot3xs">
              <h2 className="text-lg font-bold" data-id="element-1025" data-mp="lb63ndp7k2c0">User Demographics</h2>
              <p className="text-sm text-gray-400" data-id="element-1026" data-mp="gaqsmssef6x7">
                Based on {sheetUsers.length} unique profiles
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1027" data-mp="u98f6n6kiddj">
              {/* Age Distribution */}
              <div className="bg-gray-900 rounded-xl p-5 border border-gray-800" data-id="element-1028" data-mp="q7n1izm7lv2a">
                <h3 className="font-bold text-white mb-4" data-id="element-1029" data-mp="142yxfac3prks">Age Range</h3>
                <div className="space-y-3" data-id="element-1030" data-mp="z32t19zhry4p">
                  {getDistribution(sheetUsers, 'ageRange').map(([label, count]) => {
                const pct = Math.round(count / sheetUsers.length * 100);
                return <div key={label} data-id="element-1031" data-mp="1g3nrvyckr35d">
                          <div className="flex justify-between text-xs mb-1" data-id="element-1032" data-mp="1g4qg1yiiirhl">
                            <span className="text-gray-400" data-id="element-1033" data-mp="1l3a2s14xk7b0">{label}</span>
                            <span className="text-white font-bold" data-id="element-1034" data-mp="1q0tht39gco40">
                              {pct}% ({count})
                            </span>
                          </div>
                          <div className="w-full bg-gray-800 h-1.5 rounded-full overflow-hidden" data-id="element-1035" data-mp="1zr4yzri5ctsu">
                            <div className="bg-brand-blue h-full" style={{
                      width: `${pct}%`
                    }} data-id="element-1036" data-mp="23sq76zjqkjwp"></div>
                          </div>
                        </div>;
              })}
                </div>
              </div>

              {/* Occupation */}
              <div className="bg-gray-900 rounded-xl p-5 border border-gray-800" data-id="element-1037" data-mp="jdo9zdajasa4">
                <h3 className="font-bold text-white mb-4" data-id="element-1038" data-mp="bufbwg7hag0d">Occupation</h3>
                <div className="space-y-3" data-id="element-1039" data-mp="72x8zmlwa5cr">
                  {getDistribution(sheetUsers, 'occupation').map(([label, count]) => {
                const pct = Math.round(count / sheetUsers.length * 100);
                return <div key={label} data-id="element-1040" data-mp="3t7r77hov19kv">
                          <div className="flex justify-between text-xs mb-1" data-id="element-1041" data-mp="usripvax3dss">
                            <span className="text-gray-400" data-id="element-1042" data-mp="pt569o2wy1l1">{label}</span>
                            <span className="text-white font-bold" data-id="element-1043" data-mp="pr27aejs8y46">
                              {pct}% ({count})
                            </span>
                          </div>
                          <div className="w-full bg-gray-800 h-1.5 rounded-full overflow-hidden" data-id="element-1044" data-mp="fufmtqvdc92o">
                            <div className="bg-brand-yellow h-full" style={{
                      width: `${pct}%`
                    }} data-id="element-1045" data-mp="35luffraoimn5"></div>
                          </div>
                        </div>;
              })}
                </div>
              </div>

              {/* Top Interests */}
              <div className="bg-gray-900 rounded-xl p-5 border border-gray-800" data-id="element-1046" data-mp="3l9pgl0v1qpk6">
                <h3 className="font-bold text-white mb-4" data-id="element-1047" data-mp="cufi02qd82h5">Top Interests</h3>
                <div className="space-y-3" data-id="element-1048" data-mp="cwiijgp59e04">
                  {getDistribution(sheetUsers, 'interests').slice(0, 6).map(([label, count]) => {
                const pct = Math.round(count / sheetUsers.length * 100);
                return <div key={label} data-id="element-1049" data-mp="81mchcn2oq40">
                          <div className="flex justify-between text-xs mb-1" data-id="element-1050" data-mp="vsrx053yk6kn">
                            <span className="text-gray-400" data-id="element-1051" data-mp="10k06101dlpxa">{label}</span>
                            <span className="text-white font-bold" data-id="element-1052" data-mp="10m4iw7h95z9p">
                              {pct}% ({count})
                            </span>
                          </div>
                          <div className="w-full bg-gray-800 h-1.5 rounded-full overflow-hidden" data-id="element-1053" data-mp="2gasugmbfybmg">
                            <div className="bg-purple-400 h-full" style={{
                      width: `${pct}%`
                    }} data-id="element-1054" data-mp="2uzp4gox25ypp"></div>
                          </div>
                        </div>;
              })}
                </div>
              </div>

              {/* Favorite Area */}
              <div className="bg-gray-900 rounded-xl p-5 border border-gray-800" data-id="element-1055" data-mp="2c7e71al6asyz">
                <h3 className="font-bold text-white mb-4" data-id="element-1056" data-mp="27cqodscuossb">Favorite Area</h3>
                <div className="space-y-3" data-id="element-1057" data-mp="3nncifl3tpr3d">
                  {getDistribution(sheetUsers, 'favoriteArea').map(([label, count]) => {
                const pct = Math.round(count / sheetUsers.length * 100);
                return <div key={label} data-id="element-1058" data-mp="2mqajq8zaa0eq">
                          <div className="flex justify-between text-xs mb-1" data-id="element-1059" data-mp="2mp99g6q3dhy2">
                            <span className="text-gray-400" data-id="element-1060" data-mp="30nz7bokqjlnh">{label}</span>
                            <span className="text-white font-bold" data-id="element-1061" data-mp="2wnf7gd8s23xn">
                              {pct}% ({count})
                            </span>
                          </div>
                          <div className="w-full bg-gray-800 h-1.5 rounded-full overflow-hidden" data-id="element-1062" data-mp="23z9o9uxdgmzx">
                            <div className="bg-green-400 h-full" style={{
                      width: `${pct}%`
                    }} data-id="element-1063" data-mp="2hq62ggqjx1ng"></div>
                          </div>
                        </div>;
              })}
                </div>
              </div>

              {/* Pickup Preference */}
              <div className="bg-gray-900 rounded-xl p-5 border border-gray-800" data-id="element-1064" data-mp="1b3a66wxozvv5">
                <h3 className="font-bold text-white mb-4" data-id="element-1065" data-mp="1fw39dgdiiw54">Pickup Preference</h3>
                <div className="space-y-3" data-id="element-1066" data-mp="1176uazd1giv0">
                  {getDistribution(sheetUsers, 'storePreference').map(([label, count]) => {
                const pct = Math.round(count / sheetUsers.length * 100);
                return <div key={label} data-id="element-1067" data-mp="1ytw2jqyc3rpb">
                          <div className="flex justify-between text-xs mb-1" data-id="element-1068" data-mp="1ysus9op5798n">
                            <span className="text-gray-400" data-id="element-1069" data-mp="1jxoh6nb6wk8g">{label}</span>
                            <span className="text-white font-bold" data-id="element-1070" data-mp="1ovq1fbkcqpli">
                              {pct}% ({count})
                            </span>
                          </div>
                          <div className="w-full bg-gray-800 h-1.5 rounded-full overflow-hidden" data-id="element-1071" data-mp="8k2wpfg9z6t9">
                            <div className="bg-orange-400 h-full" style={{
                      width: `${pct}%`
                    }} data-id="element-1072" data-mp="degjwuksa7mu"></div>
                          </div>
                        </div>;
              })}
                </div>
              </div>

              {/* Catch Count Tiers */}
              <div className="bg-gray-900 rounded-xl p-5 border border-gray-800" data-id="element-1073" data-mp="1wgk8v4edspd6">
                <h3 className="font-bold text-white mb-4" data-id="element-1074" data-mp="1sdntdmk519dg">Catch Count Tiers</h3>
                <div className="space-y-3" data-id="element-1075" data-mp="1nekmdwklkt2u">
                  {getDistribution(sheetUsers, u => {
                if (u.catchCount === 1) return '1 Catch (New)';
                if (u.catchCount === 2) return '2 Catches';
                if (u.catchCount === 3) return '3 Catches';
                return '4+ Catches (Loyal)';
              }).map(([label, count]) => {
                const pct = Math.round(count / sheetUsers.length * 100);
                return <div key={label} data-id="element-1076" data-mp="3pc3b0cjzj27x">
                        <div className="flex justify-between text-xs mb-1" data-id="element-1077" data-mp="3pb1v9us0xrjv">
                          <span className="text-gray-400" data-id="element-1078" data-mp="3u2tetlcrix0i">{label}</span>
                          <span className="text-white font-bold" data-id="element-1079" data-mp="2ws7nnvx4nk5">
                            {pct}% ({count})
                          </span>
                        </div>
                        <div className="w-full bg-gray-800 h-1.5 rounded-full overflow-hidden" data-id="element-1080" data-mp="31gifee0w725a">
                          <div className="bg-blue-400 h-full" style={{
                      width: `${pct}%`
                    }} data-id="element-1081" data-mp="36fl8r17ifjxf"></div>
                        </div>
                      </div>;
              })}
                </div>
              </div>
            </div>
          </div>}

        {/* VERIFY TAB */}
        {tab === 'verify' && <div className="space-y-6" data-id="element-1082" data-mp="t7kd1401701q">
            <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800" data-id="element-1083" data-mp="t8lv3sfgsmsy">
              <h2 className="text-lg font-bold mb-4 flex items-center" data-id="element-1084" data-mp="p7a5hmwlc1kr">
                <Scan className="w-5 h-5 mr-2 text-blue-400" data-id="element-1085" data-mp="135rnd3ztejur" />
                Unlock Reserved Catch
              </h2>
              <form onSubmit={handleVerify} className="space-y-4" data-id="element-1086" data-mp="y4khjah8fuqq">
                <Input placeholder="Enter 4-char User Code" value={scanResult} onChange={e => setScanResult(e.target.value)} className="bg-gray-800 border-gray-700 text-white text-center text-xl" autoFocus data-id="element-1087" data-mp="1c4liepxhz267" />
                <Button type="submit" className="w-full" data-id="element-1088" data-mp="1gwwt8nts08dj">
                  Unlock Catch
                </Button>
              </form>
            </div>

            {/* Camera QR scanner — scan a user's redemption QR or a drop code */}
            <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800" data-id="element-1089" data-mp="1qsq29x8adjqk">
              <h2 className="text-lg font-bold mb-1 flex items-center" data-id="element-1090" data-mp="2ae8ehpa03jw2">
                <QrCode className="w-5 h-5 mr-2 text-brand-yellow" data-id="element-1091" data-mp="2egmywdtbirpg" />
                Scan QR Code
              </h2>
              <p className="text-gray-400 text-sm mb-4" data-id="element-1092" data-mp="20hwzusmxuhyg">
                Use the device camera to scan a customer's redemption QR (or a
                drop QR). The result shows whether the code is valid, already
                redeemed, or invalid.
              </p>

              {showScanner ? <QrScanner onResult={handleScannedCode} onClose={() => setShowScanner(false)} data-id="element-1093" data-mp="1vkdbgzwj0oss" /> : <Button onClick={() => {
            setScanVerdict(null);
            setShowScanner(true);
          }} className="w-full" data-id="element-1094" data-mp="1mrtiaez07cfc">
                  <Camera className="w-4 h-4 mr-2" data-id="element-1095" data-mp="1cs2q4jxx19if" />
                  Open camera scanner
                </Button>}

              {scanVerdict && <div className={`mt-4 rounded-xl p-4 border flex items-start ${scanVerdict.kind === 'valid' ? 'bg-green-900/30 border-green-500/50' : scanVerdict.kind === 'redeemed' ? 'bg-yellow-900/20 border-yellow-500/50' : scanVerdict.kind === 'drop' ? 'bg-blue-900/20 border-blue-500/50' : 'bg-red-900/30 border-red-500/50'}`} data-id="element-1096" data-mp="owzarw6upyhh">
                  {scanVerdict.kind === 'valid' ? <CheckCircle className="w-6 h-6 text-green-400 mr-3 shrink-0" data-id="element-1097" data-mp="17p1w3kufu2y2" /> : scanVerdict.kind === 'redeemed' ? <AlertCircle className="w-6 h-6 text-yellow-400 mr-3 shrink-0" data-id="element-1098" data-mp="12qgv3j7d3f8k" /> : scanVerdict.kind === 'drop' ? <QrCode className="w-6 h-6 text-blue-400 mr-3 shrink-0" data-id="element-1099" data-mp="3nf4ttdc6zoco" /> : <XCircle className="w-6 h-6 text-red-400 mr-3 shrink-0" data-id="element-1100" data-mp="3ififf0kmo22a" />}
                  <div className="min-w-0" data-id="element-1101" data-mp="1xyiteoy2oliq">
                    <p className={`font-bold ${scanVerdict.kind === 'valid' ? 'text-green-300' : scanVerdict.kind === 'redeemed' ? 'text-yellow-300' : scanVerdict.kind === 'drop' ? 'text-blue-300' : 'text-red-300'}`} data-id="element-1102" data-mp="1xvo86v78iwo9">
                      {scanVerdict.title}
                    </p>
                    {scanVerdict.detail && <p className="text-sm text-gray-300 mt-0.5 break-words" data-id="element-1103" data-mp="2hkypgdmmppoh">
                        {scanVerdict.detail}
                      </p>}
                    <button onClick={() => {
                setScanVerdict(null);
                setShowScanner(true);
              }} className="text-xs text-gray-400 hover:text-white mt-2 underline" data-id="element-1104" data-mp="2qm56cnt6x2i9">
                      Scan another
                    </button>
                  </div>
                </div>}
            </div>

            {verificationStatus === 'success' && verifiedUser && <div className={`rounded-2xl p-6 border ${verifiedUser.alreadyDone ? 'bg-yellow-900/20 border-yellow-500/50' : 'bg-green-900/30 border-green-500/50'}`} data-id="element-1105" data-mp="2y6pjwhz20ayy">
                <div className="flex items-center mb-4" data-id="element-1106" data-mp="37vh5879lzx0w">
                  <CheckCircle className={`w-8 h-8 mr-3 ${verifiedUser.alreadyDone ? 'text-yellow-400' : 'text-green-400'}`} data-id="element-1107" data-mp="37y3dya88kikp" />
                  <div data-id="element-1108" data-mp="2f8d62ix76cx1">
                    <h3 className="text-xl font-bold" data-id="element-1109" data-mp="2f968x8t1ci9w">
                      {verifiedUser.alreadyDone ? 'Already unlocked' : 'Catch Unlocked!'}
                    </h3>
                    <p className={`text-sm ${verifiedUser.alreadyDone ? 'text-yellow-300' : 'text-green-300'}`} data-id="element-1110" data-mp="9e3i621s26wi">
                      {verifiedUser.alreadyDone ? 'This code was redeemed earlier' : 'Valid reservation'}
                    </p>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-gray-300" data-id="element-1111" data-mp="gvjhs74dj59c">
                  <p data-id="element-1112" data-mp="2z6d5ykxuyca">
                    Customer:{' '}
                    <span className="text-white font-bold" data-id="element-1113" data-mp="6z6qth07r5ia">
                      {verifiedUser.name}
                    </span>
                    {verifiedUser.phone && <span className="text-gray-500 font-mono ml-2" data-id="element-1114" data-mp="ur40p2k3rngr">
                        {verifiedUser.phone}
                      </span>}
                  </p>
                  <p data-id="element-1115" data-mp="lwh14n14e5cf">
                    Product:{' '}
                    <span className="text-white font-bold" data-id="element-1116" data-mp="pxj1x8aoqpgi">
                      {verifiedUser.brand || verifiedUser.event || 'Catch'}
                    </span>
                  </p>
                  <p data-id="element-1117" data-mp="1iedbov44iw39">
                    Status:{' '}
                    <span className={`font-bold uppercase ${verifiedUser.status === 'reviewed' ? 'text-green-400' : 'text-yellow-400'}`} data-id="element-1118" data-mp="1ifwrkklvb54i">
                      {verifiedUser.status}
                    </span>
                  </p>
                  {!verifiedUser.alreadyDone && <p className="mt-4 text-yellow-400 font-medium" data-id="element-1119" data-mp="2xn0nx1y0e8q5">
                      The user will now be prompted to review their catch.
                    </p>}
                </div>

                <Button onClick={() => {
            setVerificationStatus('idle');
            setScanResult('');
            setVerifiedUser(null);
          }} className="w-full mt-6 bg-green-600 hover:bg-green-700 border-none" data-id="element-1120" data-mp="24y35mytssbxe">
                  Unlock Next Catch
                </Button>
              </div>}

            {verificationStatus === 'error' && <div className="bg-red-900/30 border border-red-500/50 rounded-2xl p-6" data-id="element-1121" data-mp="3q9utksq633jk">
                <div className="flex items-center mb-2" data-id="element-1122" data-mp="1en15zixlx64b">
                  <XCircle className="w-8 h-8 text-red-400 mr-3" data-id="element-1123" data-mp="19pj061evz70j" />
                  <h3 className="text-xl font-bold" data-id="element-1124" data-mp="19rc9jogvw5c8">Invalid</h3>
                </div>
                <p className="text-red-300 text-sm mb-4" data-id="element-1125" data-mp="14wpzbomjgitq">
                  No reservation found.
                </p>
                <Button onClick={() => setVerificationStatus('idle')} variant="secondary" className="w-full bg-red-900/50 text-red-200 border-red-800" data-id="element-1126" data-mp="zxlq4xzm6l3e">
                  Try Again
                </Button>
              </div>}

            {/* Recurring-member verification — confirm a returning-user claim. */}
            <AdminVerifyMember users={sheetUsers} catches={sheetCatches} data-id="element-1127" data-mp="h2on3pkef9x3" />

            {/* QR codes — one per drop code, deep-linking into registration. */}
            <DropQrCodes events={events} data-id="element-1128" data-mp="2bfo8jzbgmyca" />
          </div>}

        {/* USERS TAB */}
        {tab === 'users' && <div className="space-y-4" data-id="element-1129" data-mp="9o4hwyufyi3y">
            <div className="flex justify-between items-center" data-id="element-1130" data-mp="efvz4irpjbhf">
              <h2 className="text-lg font-bold" data-id="element-1131" data-mp="ehguubprd0gv">
                {totalUsers} Registered Users
              </h2>
              <button onClick={exportUsers} className="flex items-center text-sm text-blue-400 hover:text-blue-300" data-id="element-1132" data-mp="3vusyq1l7h2cz">
                <Download className="w-4 h-4 mr-1" data-id="element-1133" data-mp="sh8bylbkmwz3" /> Export
              </button>
            </div>

            <div className="overflow-x-auto -mx-6" data-id="element-1134" data-mp="xdozpeeuz9bn">
              <table className="w-full text-sm" data-id="element-1135" data-mp="jfin9n92qc50">
                <thead data-id="element-1136" data-mp="jepc8a3310h2">
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-800" data-id="element-1137" data-mp="jhlc43yhpz85">
                    <th className="px-6 py-3" data-id="element-1138" data-mp="345hkp2sht2td">User</th>
                    <th className="px-4 py-3" data-id="element-1139" data-mp="343wo77mmb9sv">Phone</th>
                    <th className="px-4 py-3" data-id="element-1140" data-mp="387kvdp7c83dl">Catches</th>
                    <th className="px-4 py-3" data-id="element-1141" data-mp="384ynfogjbluu">Attendance</th>
                    <th className="px-4 py-3" data-id="element-1142" data-mp="30mip38e0sytq">Points</th>
                    <th className="px-4 py-3" data-id="element-1143" data-mp="2voh6spof53jb">Status</th>
                    <th className="px-6 py-3" data-id="element-1144" data-mp="2vnnyhk9ta5z2"></th>
                  </tr>
                </thead>
                <tbody data-id="element-1145" data-mp="3akf0806yjtru">
                  {sheetUsers.map((u, i) => {
                const userReservations = sheetCatches.filter(r => r.userId === u.phone);
                const isReturning = u.catchCount >= 2;
                // Eligibility derived from the sheet: has a reviewed catch.
                const isEligible = userReservations.some(r => r.status === 'reviewed');
                const isExpanded = expandedUserPhone === u.phone;
                return <Fragment key={i} data-id="element-1146" data-mp="1z5jb1kaoq9x1" data-mpi={i}>
                        <tr onClick={() => toggleUserExpanded(u.phone)} className={`border-b border-gray-800/50 hover:bg-gray-900/50 cursor-pointer transition-colors ${isExpanded ? 'bg-gray-900/50' : ''}`} data-id="element-1147" data-mp="1z4glpgt0gfj8">
                          <td className="px-6 py-4" data-id="element-1148" data-mp="11hy3slufdjdv">
                            <div className="font-medium text-white whitespace-nowrap" data-id="element-1149" data-mp="16gj6crx5gf5h">
                              {u.name}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5" data-id="element-1150" data-mp="1b9cbhgwj5pi3">
                              {u.city} • {u.ageRange}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-gray-400 font-mono text-xs" data-id="element-1151" data-mp="1l7s1fgjtmsq0">
                            {u.phone}
                          </td>
                          <td className="px-4 py-4" data-id="element-1152" data-mp="1u5asb1k5c19t">
                            <div className="flex items-center space-x-1" data-id="element-1153" data-mp="1u3znqzivmphn">
                              <Package className="w-4 h-4 text-blue-400" data-id="element-1154" data-mp="3r0gmzlmoqbi4" />
                              <span className="font-bold text-white" data-id="element-1155" data-mp="3vyrw1u55ohza">
                                {u.catchCount || 1}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4" data-id="element-1156" data-mp="3jm6c2uot2d0h">
                            {isReturning ? <span className="bg-purple-500/20 text-purple-400 text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider" data-id="element-1157" data-mp="3eoo16yr89npu">
                                Returning
                              </span> : <span className="text-gray-500 text-xs font-medium" data-id="element-1158" data-mp="68b9rn5nyo49">
                                First Catch
                              </span>}
                          </td>
                          <td className="px-4 py-4 font-bold text-white" data-id="element-1159" data-mp="3tcr6s71cyxe4">
                            {u.points}
                          </td>
                          <td className="px-4 py-4" data-id="element-1160" data-mp="2hy5b3opca2tr">
                            <span className={`text-[10px] font-bold px-2 py-1 rounded ${isEligible ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'}`} data-id="element-1161" data-mp="2hwkfrwv7afuu">
                              {isEligible ? 'ELIGIBLE' : 'PENDING'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right" data-id="element-1162" data-mp="2efolhinbzrf8">
                            {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400 inline-block" data-id="element-1163" data-mp="20ojge11726xi" /> : <ChevronDown className="w-5 h-5 text-gray-400 inline-block" data-id="element-1164" data-mp="25h2vj2fj5a4f" />}
                          </td>
                        </tr>

                        {/* Expanded Details Row */}
                        {isExpanded && <tr className="bg-gray-900/30 border-b border-gray-800/50" data-id="element-1165" data-mp="1hpp36whdpejg">
                            <td colSpan={7} className="px-6 py-6" data-id="element-1166" data-mp="1ls25tc0twwc0">
                              <div className="mb-6 bg-gray-900 rounded-xl p-4 border border-gray-800 flex items-center space-x-2" data-id="element-1167" data-mp="1ltd9lbu9y434">
                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider mr-2" data-id="element-1168" data-mp="y4otgisx6oub">
                                  Persona:
                                </span>
                                <div className="flex flex-wrap gap-2" data-id="element-1169" data-mp="174bzjhwlgitp">
                                  {[u.ageRange, u.occupation, u.favoriteArea, ...(u.interests || [])].filter(Boolean).map((tag, idx) => <span key={idx} className="bg-brand-blue/20 text-brand-blue text-[10px] px-2 py-1 rounded-full font-medium" data-id="element-1170" data-mp="1qv6wjjtn8q7q" data-mpi={idx}>
                                        {tag}
                                      </span>)}
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1171" data-mp="3g7xy37ifjzy4">
                                {/* Profile Info */}
                                <div className="space-y-4" data-id="element-1172" data-mp="2nhz7h7ol7urc">
                                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-1173" data-mp="2njk1mw6zpboo">
                                    Profile Details
                                  </h4>
                                  <div className="bg-gray-900 rounded-xl p-4 border border-gray-800 space-y-3" data-id="element-1174" data-mp="2xeke1xg99dxa">
                                    <div data-id="element-1175" data-mp="3286gyxwma3fw">
                                      <p className="text-xs text-gray-500" data-id="element-1176" data-mp="24rzmowzs51tq">
                                        Occupation
                                      </p>
                                      <p className="text-sm text-white font-medium" data-id="element-1177" data-mp="29o7wduv7k2ti">
                                        {u.occupation || 'Not specified'}
                                      </p>
                                    </div>
                                    <div data-id="element-1178" data-mp="1s82p0pufj0e5">
                                      <p className="text-xs text-gray-500" data-id="element-1179" data-mp="1s7129o41wpgl">
                                        Profession / Studies
                                      </p>
                                      <p className="text-sm text-white font-medium" data-id="element-1180" data-mp="1j8z3lf7si8wh">
                                        {u.profession || 'Not specified'}
                                      </p>
                                    </div>
                                    <div data-id="element-1181" data-mp="14jb1wcvp5888">
                                      <p className="text-xs text-gray-500" data-id="element-1182" data-mp="14kcc6f4w1qow">
                                        Lives in
                                      </p>
                                      <p className="text-sm text-white font-medium" data-id="element-1183" data-mp="viv1jca92h7l">
                                        {u.city || 'Not specified'}
                                      </p>
                                    </div>
                                    <div data-id="element-1184" data-mp="11mbulo8km32g">
                                      <p className="text-xs text-gray-500" data-id="element-1185" data-mp="11l0o3gnqqh7n">
                                        Works in
                                      </p>
                                      <p className="text-sm text-white font-medium" data-id="element-1186" data-mp="1bcn7w1e59om0">
                                        {u.workArea || 'Not specified'}
                                      </p>
                                    </div>
                                    <div data-id="element-1187" data-mp="nnltzymjll9c">
                                      <p className="text-xs text-gray-500" data-id="element-1188" data-mp="nmkglni20mog">
                                        Usual area
                                      </p>
                                      <p className="text-sm text-white font-medium" data-id="element-1189" data-mp="1z310pu4jzrhn">
                                        {u.favoriteArea || 'Not specified'}
                                      </p>
                                    </div>
                                    <div data-id="element-1190" data-mp="3ojd3c8rdzivc">
                                      <p className="text-xs text-gray-500" data-id="element-1191" data-mp="3jktf21pbluzt">
                                        Pickup Preference
                                      </p>
                                      <p className="text-sm text-white font-medium" data-id="element-1192" data-mp="3es0c9jdewwqd">
                                        {u.storePreference || 'Not specified'}
                                      </p>
                                    </div>
                                    <div data-id="element-1193" data-mp="30u2b5fo3qwst">
                                      <p className="text-xs text-gray-500 mb-1" data-id="element-1194" data-mp="30t0oedxq4lv9">
                                        Interests
                                      </p>
                                      <div className="flex flex-wrap gap-1" data-id="element-1195" data-mp="2qx5wstbivggc">
                                        {(u.interests || []).map(int => <span key={int} className="bg-blue-500/20 text-blue-300 text-[10px] px-1.5 py-0.5 rounded" data-id="element-1196" data-mp="p4rjk7jxb069">
                                            {int}
                                          </span>)}
                                      </div>
                                    </div>
                                    {u.invitedBy && <div data-id="element-1197" data-mp="32cskcj69ea8t">
                                        <p className="text-xs text-gray-500 mb-1" data-id="element-1198" data-mp="329mqkqmoqk6r">
                                          Invited By
                                        </p>
                                        <p className="text-sm text-white font-mono" data-id="element-1199" data-mp="2sd1ke1yxj31c">
                                          {u.invitedBy}
                                        </p>
                                      </div>}
                                    {u.partnerCode && <div data-id="element-1200" data-mp="3zm957bobbia">
                                        <p className="text-xs text-gray-500 mb-1" data-id="element-1201" data-mp="3yjfirmyuuyo">
                                          Partner Code
                                        </p>
                                        <p className="text-sm text-brand-yellow font-mono font-bold" data-id="element-1202" data-mp="d0kebsxulp2g">
                                          {u.partnerCode}
                                        </p>
                                      </div>}
                                    {Object.keys(u.customAnswers || {}).length > 0 && <div data-id="element-1203" data-mp="3q7uhiwtjnt8e">
                                        <p className="text-xs text-gray-500 mb-1" data-id="element-1204" data-mp="3q8ni1g1wtmi3">
                                          Question Answers
                                        </p>
                                        <div className="space-y-2" data-id="element-1205" data-mp="2xoouq59497s0">
                                          {Object.entries(u.customAnswers || {}).map(([qid, answer]) => <div key={qid} className="bg-gray-950 rounded-lg p-2 border border-gray-800" data-id="element-1206" data-mp="3bea49kji4c94">
                                              <p className="text-[11px] text-gray-500 leading-tight" data-id="element-1207" data-mp="3q24yx6h2kqou">
                                                {questionLabelMap[qid] || qid}
                                              </p>
                                              <p className="text-sm text-white font-medium" data-id="element-1208" data-mp="2x5it2jznmzd">
                                                {answer}
                                              </p>
                                            </div>)}
                                        </div>
                                      </div>}
                                  </div>
                                </div>

                                {/* Catch History */}
                                <div className="md:col-span-2 space-y-4" data-id="element-1209" data-mp="1xbklwa07d1ou">
                                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-1210" data-mp="1sf2fz6t4yxax">
                                    Catch History
                                  </h4>

                                  {userReservations.length === 0 ? <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 text-center text-gray-500 text-sm" data-id="element-1211" data-mp="2g3vixn86e5f1">
                                      No catches recorded yet.
                                    </div> : <div className="space-y-3" data-id="element-1212" data-mp="zpn3ecltowcw">
                                      {userReservations.map(res => {
                              const userReviews = sheetReviews.filter(r => r.reservationId === res.id);
                              let statusColor = 'bg-gray-700 text-gray-300';
                              if (res.status === 'redeemed') statusColor = 'bg-yellow-500/20 text-yellow-400';
                              if (res.status === 'reviewed') statusColor = 'bg-green-500/20 text-green-400';
                              return <div key={res.id} className="bg-gray-900 rounded-xl p-4 border border-gray-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1213" data-mp="2v880282ywzup">
                                            <div className="flex items-start space-x-3" data-id="element-1214" data-mp="21np93lpzh2h7">
                                              <div className="bg-gray-800 p-2 rounded-lg mt-1" data-id="element-1215" data-mp="1xmwyop5dkrjc">
                                                <Calendar className="w-4 h-4 text-gray-400" data-id="element-1216" data-mp="2bm63yts1m18j" />
                                              </div>
                                              <div data-id="element-1217" data-mp="2km1ldgc6ysh0">
                                                <h5 className="font-bold text-white text-sm" data-id="element-1218" data-mp="2kkqh7feu3ipd">
                                                  {res.eventTitle || 'Unknown Event'}
                                                </h5>
                                                <p className="text-xs text-gray-400" data-id="element-1219" data-mp="2pdt2l09gjluh">
                                                  {res.eventBrand} •{' '}
                                                  {new Date(res.timestamp).toLocaleDateString()}
                                                </p>
                                                <div className="flex items-center mt-2 space-x-2 flex-wrap gap-y-1" data-id="element-1220" data-mp="1owepc94wnskg">
                                                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${statusColor}`} data-id="element-1221" data-mp="1txkk0ci4lpwm">
                                                    {res.status}
                                                  </span>
                                                  {res.recurring && <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider bg-brand-yellow/20 text-brand-yellow" data-id="element-1222" data-mp="1fxjntact36nh">
                                                      Reserved spot
                                                    </span>}
                                                  <span className="text-xs text-gray-500 font-mono" data-id="element-1223" data-mp="30ow8if5bpvy5">
                                                    Code: {res.userCode}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>

                                            <div className="bg-gray-950 rounded-lg p-3 border border-gray-800 min-w-[140px]" data-id="element-1224" data-mp="2htpat5u4bpft">
                                              <div className="flex items-center justify-between mb-1" data-id="element-1225" data-mp="2czl1wh1al7c3">
                                                <span className="text-xs text-gray-400" data-id="element-1226" data-mp="28xgfo3d6bczh">
                                                  Reviews
                                                </span>
                                                <span className="text-xs font-bold text-white" data-id="element-1227" data-mp="1u2jvqsw0516k">
                                                  {userReviews.length} items
                                                </span>
                                              </div>
                                              {res.status === 'reviewed' ? <div className="flex items-center text-green-400 text-xs font-medium" data-id="element-1228" data-mp="1cyenc3rst71s">
                                                  <CheckCircle className="w-3 h-3 mr-1" data-id="element-1229" data-mp="17xqxvmj7wy9o" />{' '}
                                                  Completed
                                                </div> : res.status === 'redeemed' ? <div className="flex items-center text-yellow-400 text-xs font-medium" data-id="element-1230" data-mp="u1dh6fmid0w9">
                                                  <Star className="w-3 h-3 mr-1" data-id="element-1231" data-mp="p7j0ll27921e" />{' '}
                                                  Pending Review
                                                </div> : <div className="text-gray-500 text-xs italic" data-id="element-1232" data-mp="2878iennueh3t">
                                                  Not collected
                                                </div>}
                                            </div>
                                          </div>;
                            })}
                                    </div>}
                                </div>
                              </div>
                            </td>
                          </tr>}
                      </Fragment>;
              })}
                </tbody>
              </table>
            </div>
          </div>}

        {/* PARTNERS TAB */}
        {tab === 'partners' && <div className="space-y-6" data-id="element-1233" data-mp="1bisoyghm3nlf">
            <div className="flex justify-between items-center" data-id="element-1234" data-mp="1bfog35t7c3of">
              <h2 className="text-lg font-bold" data-id="element-1235" data-mp="red4206x8y9i">Partner Codes</h2>
              <Button onClick={() => setShowAddPartner(!showAddPartner)} className="text-sm py-1.5 px-3 flex items-center" data-id="element-1236" data-mp="neb8m8rs512o">
                {showAddPartner ? 'Cancel' : <>
                    <Plus className="w-4 h-4 mr-1" data-id="element-1237" data-mp="8p5egz7su11n" /> Add Partner
                  </>}
              </Button>
            </div>

            {showAddPartner && <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-6" data-id="element-1238" data-mp="3s6monbly66m">
                <h3 className="font-bold mb-4" data-id="element-1239" data-mp="3vueh2gqputs2">Add New Partner Code</h3>
                <form onSubmit={e => {
            e.preventDefault();
            const code = newPartner.code.trim().toUpperCase();
            if (!code) return;
            // Check for duplicates
            if (partnerCodes.some(p => p.code.toUpperCase() === code)) {
              alert('This code already exists.');
              return;
            }
            setPartnerCodes([...partnerCodes, {
              ...newPartner,
              code,
              active: true
            }]);
            setShowAddPartner(false);
            setNewPartner({
              code: '',
              name: '',
              type: 'brand',
              address: '',
              city: '',
              postalCode: ''
            });
          }} className="space-y-4" data-id="element-1240" data-mp="3qvtjyq6rgq7u">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1241" data-mp="2z154s6yeo6tj">
                    <div data-id="element-1242" data-mp="331ozl3viayci">
                      <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1243" data-mp="3316xhqm5xpwo">
                        Partner Code
                      </label>
                      <Input required placeholder="e.g. SUMMER26" value={newPartner.code} onChange={e => setNewPartner({
                  ...newPartner,
                  code: e.target.value.toUpperCase()
                })} className="bg-gray-800 border-gray-700 text-white font-mono uppercase" data-id="element-1244" data-mp="2wnt7laf8umcn" />
                    </div>
                    <div data-id="element-1245" data-mp="3uccreppx8ayd">
                      <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1246" data-mp="3pavqwvbcmqib">
                        Store / Partner Name
                      </label>
                      <Input required placeholder="e.g. 7-Eleven" value={newPartner.name} onChange={e => setNewPartner({
                  ...newPartner,
                  name: e.target.value
                })} className="bg-gray-800 border-gray-700 text-white" data-id="element-1247" data-mp="73g1iqs5js1o" />
                    </div>
                    <div data-id="element-1248" data-mp="329t3pdh3lmv">
                      <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1249" data-mp="31rr0c44qd71">
                        Type
                      </label>
                      <select value={newPartner.type} onChange={e => setNewPartner({
                  ...newPartner,
                  type: e.target.value as 'brand' | 'event'
                })} className="w-full p-2 rounded-lg bg-gray-800 border border-gray-700 text-white outline-none focus:border-blue-500" data-id="element-1250" data-mp="7uktsug1fbgh">
                        <option value="brand" data-id="element-1251" data-mp="1qtimn52l9r1l">Brand / Store</option>
                        <option value="event" data-id="element-1252" data-mp="1lzwcbjjwdz8y">Event</option>
                      </select>
                    </div>
                  </div>

                  {/* Location — this is the partner's identity in the portal */}
                  <div className="border-t border-gray-800 pt-4" data-id="element-1253" data-mp="1d1ubb405z6lo">
                    <h4 className="text-sm font-bold text-white mb-1" data-id="element-1254" data-mp="29p8tp6pdpdmc">
                      Location
                    </h4>
                    <p className="text-xs text-gray-500 mb-3" data-id="element-1255" data-mp="25pgfaddb0d92">
                      The store/location this partner represents (e.g. 7-Eleven
                      – Drottninggatan 56, Stockholm). They sign in at{' '}
                      <span className="font-mono text-gray-400" data-id="element-1256" data-mp="1vsv93opjsw3n">/partner</span>{' '}
                      with their code to see their redemptions.
                    </p>
                    <div className="space-y-3" data-id="element-1257" data-mp="k9r9s2a09254">
                      <div data-id="element-1258" data-mp="a7ngjzd8ajkp">
                        <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1259" data-mp="a8gkytopswzo">
                          Street address
                        </label>
                        <AddressAutocomplete value={newPartner.address} onChange={v => setNewPartner(p => ({
                    ...p,
                    address: v
                  }))} onSelect={parts => setNewPartner(p => ({
                    ...p,
                    address: parts.street || parts.label,
                    city: parts.city || p.city,
                    postalCode: parts.postalCode || p.postalCode
                  }))} placeholder="Start typing the store address…" data-id="element-1260" data-mp="k5uusag4unj0" />
                      </div>
                      <div className="grid grid-cols-2 gap-3" data-id="element-1261" data-mp="1fcu6vlkgufjl">
                        <div data-id="element-1262" data-mp="1agdf8he25j1r">
                          <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1263" data-mp="1afbu1k3bvg6b">
                            City
                          </label>
                          <Input placeholder="e.g. Stockholm" value={newPartner.city} onChange={e => setNewPartner(p => ({
                      ...p,
                      city: e.target.value
                    }))} className="bg-gray-800 border-gray-700 text-white" data-id="element-1264" data-mp="1k555ml3gzvak" />
                        </div>
                        <div data-id="element-1265" data-mp="2gtunibf3knwj">
                          <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1266" data-mp="2cu2ooqfi9w4d">
                            Postal code
                          </label>
                          <Input placeholder="e.g. 111 20" value={newPartner.postalCode} onChange={e => setNewPartner(p => ({
                      ...p,
                      postalCode: e.target.value
                    }))} className="bg-gray-800 border-gray-700 text-white" data-id="element-1267" data-mp="2kca9dce2p9t8" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <Button type="submit" className="w-full mt-4" data-id="element-1268" data-mp="2tcff7i3mc05a">
                    Save Partner
                  </Button>
                </form>
              </div>}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1269" data-mp="3m1mfaliq0xsn">
              {partnerCodes.map(partner => {
            const usersJoined = sheetUsers.filter(u => u.partnerCode?.toUpperCase() === partner.code.toUpperCase());
            const redemptions = sheetCatches.filter(c => {
              const u = sheetUsers.find(su => su.phone === c.userId);
              return u?.partnerCode?.toUpperCase() === partner.code.toUpperCase() && (c.status === 'redeemed' || c.status === 'reviewed');
            });
            return <div key={partner.code} className={`bg-gray-900 border border-gray-800 p-5 rounded-xl flex flex-col ${!partner.active ? 'opacity-60' : ''}`} data-id="element-1270" data-mp="tkoodqy6oblf">
                    <div className="flex justify-between items-start mb-3" data-id="element-1271" data-mp="2ny7tpmyggyha">
                      <div data-id="element-1272" data-mp="2sxss9w9pr4th">
                        <h3 className="font-bold text-white text-lg" data-id="element-1273" data-mp="2sy2jtoaepk1a">
                          {partner.name}
                        </h3>
                        <p className="text-xs text-gray-400 uppercase tracking-wider" data-id="element-1274" data-mp="haqnwjhwthxr">
                          {partner.type}
                        </p>
                        {partnerLocationLabel(partner) && <p className="text-xs text-gray-400 mt-1.5 flex items-start gap-1" data-id="element-1275" data-mp="r23frb3pk83s">
                            <MapPin className="w-3.5 h-3.5 text-gray-500 mt-0.5 shrink-0" data-id="element-1276" data-mp="m7h374lw49i4" />
                            <span data-id="element-1277" data-mp="zz42zef1k05n">{partnerLocationLabel(partner)}</span>
                          </p>}
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider ${partner.active ? 'bg-green-500/20 text-green-400' : 'bg-gray-800 text-gray-500'}`} data-id="element-1278" data-mp="19zedusdlsvhn">
                        {partner.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <div className="bg-gray-800/50 rounded-lg p-3 mb-4 flex items-center justify-between" data-id="element-1279" data-mp="36traczv91ojf">
                      <span className="font-mono text-brand-yellow font-bold tracking-wider" data-id="element-1280" data-mp="4bri7yydujzs">
                        {partner.code}
                      </span>
                      <button onClick={() => copyCode(partner.code)} className="text-gray-400 hover:text-white" data-id="element-1281" data-mp="999jd365rux8">
                        {copiedCode === partner.code ? <CheckCircle className="w-4 h-4 text-green-400" data-id="element-1282" data-mp="nalun1adj8cs" /> : <Copy className="w-4 h-4" data-id="element-1283" data-mp="s1lwmkycogm5" />}
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mb-4" data-id="element-1284" data-mp="wz3u9f6x39eu">
                      <div className="bg-gray-950 rounded-lg p-3 border border-gray-800" data-id="element-1285" data-mp="1201mkkln5ju2">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-1286" data-mp="16ycvmt443qb8">
                          Users Joined
                        </p>
                        <p className="text-xl font-bold text-white" data-id="element-1287" data-mp="2rhonwkn2bss0">
                          {usersJoined.length}
                        </p>
                      </div>
                      <div className="bg-gray-950 rounded-lg p-3 border border-gray-800" data-id="element-1288" data-mp="2hi7q0pe1yj6l">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-1289" data-mp="2cjmkqbjy19ba">
                          Redemptions
                        </p>
                        <p className="text-xl font-bold text-white" data-id="element-1290" data-mp="3a63hnforlkwi">
                          {redemptions.length}
                        </p>
                      </div>
                    </div>

                    <div className="mt-auto flex space-x-2" data-id="element-1291" data-mp="30ga5odkpn3rq">
                      <Button variant="secondary" className="flex-1 text-xs py-1.5" onClick={() => {
                  setPartnerCodes(partnerCodes.map(p => p.code === partner.code ? {
                    ...p,
                    active: !p.active
                  } : p));
                }} data-id="element-1292" data-mp="2vi8m7rjdh2fq">
                        {partner.active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button variant="secondary" className="flex-1 text-xs py-1.5 bg-red-900/20 text-red-400 border-red-900/50 hover:bg-red-900/40" onClick={() => {
                  if (window.confirm(`Delete partner code ${partner.code}?`)) {
                    setPartnerCodes(partnerCodes.filter(p => p.code !== partner.code));
                  }
                }} data-id="element-1293" data-mp="3rgfj8dbid82d">
                        Delete
                      </Button>
                    </div>
                  </div>;
          })}
              {partnerCodes.length === 0 && !showAddPartner && <div className="col-span-full bg-gray-900 rounded-xl p-8 text-center border border-gray-800 border-dashed" data-id="element-1294" data-mp="1kr41um5kzbrf">
                  <p className="text-gray-500" data-id="element-1295" data-mp="1fskc0anv9fts">No partner codes created yet.</p>
                  <p className="text-xs text-gray-600 mt-1" data-id="element-1296" data-mp="1trtuxi3gio1g">
                    Create codes for brands or events to track their referrals.
                  </p>
                </div>}
            </div>
          </div>}

        {/* REFERRALS TAB */}
        {tab === 'referrals' && <div className="space-y-6" data-id="element-1297" data-mp="315ng73uvb7ny">
            <div className="flex justify-between items-center" data-id="element-1298" data-mp="314lv06k514si">
              <h2 className="text-lg font-bold" data-id="element-1299" data-mp="1xuzl60karykp">Referral Leaderboard</h2>
              <p className="text-sm text-gray-400" data-id="element-1300" data-mp="1xwu5bcnqvmnq">
                Users who have invited others
              </p>
            </div>
            <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden" data-id="element-1301" data-mp="27s46xza8ds0z">
              <div className="overflow-x-auto" data-id="element-1302" data-mp="2bur6kmd58h8q">
                <table className="w-full text-sm" data-id="element-1303" data-mp="2btg2elfsd7h3">
                  <thead data-id="element-1304" data-mp="1f4i4wvgnivg8">
                    <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-800 bg-gray-900/50" data-id="element-1305" data-mp="1f59pzccmhas9">
                      <th className="px-6 py-4 font-bold" data-id="element-1306" data-mp="1k0p3222t32nm">User</th>
                      <th className="px-6 py-4 font-bold" data-id="element-1307" data-mp="1o3v9b9px4tu3">Phone</th>
                      <th className="px-6 py-4 font-bold" data-id="element-1308" data-mp="1svwslfli7ktm">Invites</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1309" data-mp="wcfp1mbu3j71">
                    {(() => {
                  const referralCounts = sheetUsers.reduce((acc, u) => {
                    if (u.invitedBy) {
                      acc[u.invitedBy] = (acc[u.invitedBy] || 0) + 1;
                    }
                    return acc;
                  }, {} as Record<string, number>);
                  const leaders = Object.entries(referralCounts).map(([phone, count]) => {
                    const user = sheetUsers.find(u => u.phone.replace(/\s/g, '') === phone);
                    return {
                      phone,
                      count,
                      name: user?.name || 'Unknown'
                    };
                  }).sort((a, b) => b.count - a.count);
                  if (leaders.length === 0) {
                    return <tr data-id="element-1310" data-mp="3idjv3iynvzt5">
                            <td colSpan={3} className="px-6 py-8 text-center text-gray-500" data-id="element-1311" data-mp="3idae2kvlh2zl">
                              No referrals recorded yet.
                            </td>
                          </tr>;
                  }
                  return leaders.map((l, i) => <tr key={i} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors" data-id="element-1312" data-mp="160e4h4yjj3lk" data-mpi={i}>
                          <td className="px-6 py-4 font-medium text-white" data-id="element-1313" data-mp="w5cc808yxler">
                            {l.name}
                          </td>
                          <td className="px-6 py-4 text-gray-400 font-mono" data-id="element-1314" data-mp="mdyfu1cbd1pu">
                            {l.phone}
                          </td>
                          <td className="px-6 py-4" data-id="element-1315" data-mp="8eyt4z8d2wwb">
                            <span className="bg-brand-yellow/20 text-brand-yellow px-3 py-1 rounded-full font-bold text-xs" data-id="element-1316" data-mp="de32bpvacumn">
                              {l.count}
                            </span>
                          </td>
                        </tr>);
                })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>}

        {/* REVIEWS TAB */}
        {tab === 'insights' && <BehaviorInsights users={sheetUsers} catches={sheetCatches} reviews={sheetReviews} onExport={exportJoinedBehavior} data-id="element-1317" data-mp="3uylba7pltmhr" />}

        {tab === 'reviews' && (() => {
        const allBrands = Array.from(new Set(sheetReviews.map(r => r.productBrand).filter(b => b && b.trim() !== ''))).sort();
        const activeBrand = reviewBrandFilter && allBrands.includes(reviewBrandFilter) ? reviewBrandFilter : '';
        const visibleReviews = activeBrand ? sheetReviews.filter(r => r.productBrand === activeBrand) : sheetReviews;
        const productStats = visibleReviews.reduce((acc, r) => {
          const key = `${r.productBrand}::${r.productName}`;
          if (!acc[key]) {
            acc[key] = {
              name: r.productName,
              brand: r.productBrand,
              total: 0,
              tasteLikes: 0,
              packagingLikes: 0,
              buyAgainLikes: 0
            };
          }
          acc[key].total += 1;
          if (r.taste) acc[key].tasteLikes += 1;
          if (r.packaging) acc[key].packagingLikes += 1;
          if (r.buyAgain) acc[key].buyAgainLikes += 1;
          return acc;
        }, {} as Record<string, any>);
        const byBrand = Object.values(productStats).reduce((acc, stats: any) => {
          const brand = stats.brand || 'Unbranded';
          if (!acc[brand]) acc[brand] = [];
          acc[brand].push(stats);
          return acc;
        }, {} as Record<string, any[]>);
        const brandSections = Object.entries(byBrand).sort((a, b) => a[0].localeCompare(b[0]));
        return <div className="space-y-6" data-id="element-1318" data-mp="2f0j79qzznt7k">
                <div className="flex justify-between items-center" data-id="element-1319" data-mp="2j3pofton34st">
                  <h2 className="text-lg font-bold" data-id="element-1320" data-mp="2j1uyu22faoie">Product Performance</h2>
                  <button onClick={exportReviews} className="flex items-center text-sm text-blue-400 hover:text-blue-300" data-id="element-1321" data-mp="2o1zig0ymnzcu">
                    <Download className="w-4 h-4 mr-1" data-id="element-1322" data-mp="1vdvcdec59k9m" /> Export Raw
                  </button>
                </div>

                {/* Brand filter — narrows both the cards and the table below */}
                {allBrands.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-1323" data-mp="33maqcm5dt54x">
                    <button onClick={() => setReviewBrandFilter('')} className={`text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider transition-colors ${activeBrand === '' ? 'bg-brand-blue text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`} data-id="element-1324" data-mp="2yor6n6px005l">
                      All brands
                    </button>
                    {allBrands.map(b => <button key={b} onClick={() => setReviewBrandFilter(b)} className={`text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider transition-colors ${activeBrand === b ? 'bg-brand-blue text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`} data-id="element-1325" data-mp="2tqfzx4ux25rl">
                        {b}
                      </button>)}
                  </div>}

                {/* Per-brand sections, each with a summary + its products */}
                {brandSections.length === 0 && <div className="text-center py-8 text-gray-500" data-id="element-1326" data-mp="2ki0lrakgf7q">
                    No reviews collected yet.
                  </div>}
                {brandSections.map(([brand, items]) => {
            const brandTotal = items.reduce((s: number, p: any) => s + p.total, 0);
            const brandBuy = items.reduce((s: number, p: any) => s + p.buyAgainLikes, 0);
            const brandBuyPct = brandTotal > 0 ? Math.round(brandBuy / brandTotal * 100) : 0;
            return <div key={brand} className="space-y-4" data-id="element-1327" data-mp="1iyi2h7sz2exm">
                      <div className="flex items-center justify-between border-b border-gray-800 pb-2" data-id="element-1328" data-mp="3qhtc4q4vha9t">
                        <h3 className="font-bold text-white flex items-center" data-id="element-1329" data-mp="3mg7s8ktx7vq4">
                          <Tag className="w-4 h-4 mr-2 text-brand-yellow" data-id="element-1330" data-mp="3miaohmlo2l3a" />
                          {brand}
                        </h3>
                        <p className="text-xs text-gray-400" data-id="element-1331" data-mp="3cijtzkx3w637">
                          {items.length} product
                          {items.length !== 1 ? 's' : ''} • {brandTotal} review
                          {brandTotal !== 1 ? 's' : ''} • {brandBuyPct}% would
                          buy again
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1332" data-mp="48ieex5buy5w">
                        {items.slice().sort((a: any, b: any) => b.total - a.total).map((stats: any) => {
                  const tastePct = Math.round(stats.tasteLikes / stats.total * 100);
                  const packPct = Math.round(stats.packagingLikes / stats.total * 100);
                  const buyPct = Math.round(stats.buyAgainLikes / stats.total * 100);
                  return <div key={`${stats.brand}::${stats.name}`} className="bg-gray-900 rounded-xl p-5 border border-gray-800" data-id="element-1333" data-mp="1i83k0lx3i4nw">
                                <div className="mb-4" data-id="element-1334" data-mp="1wzlyuge7xt5o">
                                  <h3 className="font-bold text-white text-lg" data-id="element-1335" data-mp="1wyayyrnw95ju">
                                    {stats.name}
                                  </h3>
                                  <p className="text-xs text-gray-400" data-id="element-1336" data-mp="260si2x895i2m">
                                    {stats.brand} • {stats.total} reviews
                                  </p>
                                </div>
                                <div className="space-y-3" data-id="element-1337" data-mp="1xh46fgggfbd">
                                  <div data-id="element-1338" data-mp="fnknaxk1fswl">
                                    <div className="flex justify-between text-xs mb-1" data-id="element-1339" data-mp="folyd2120feb">
                                      <span className="text-gray-400" data-id="element-1340" data-mp="av1aw3x373td">
                                        Liked Taste
                                      </span>
                                      <span className="text-white font-bold" data-id="element-1341" data-mp="kn70x83s3b5x">
                                        {tastePct}%
                                      </span>
                                    </div>
                                    <div className="w-full bg-gray-800 h-1.5 rounded-full overflow-hidden" data-id="element-1342" data-mp="ulmpp4fc28c9">
                                      <div className="bg-green-400 h-full" style={{
                            width: `${tastePct}%`
                          }} data-id="element-1343" data-mp="30ebfxmt56fap"></div>
                                    </div>
                                  </div>
                                  <div data-id="element-1344" data-mp="2chf2w99fzudw">
                                    <div className="flex justify-between text-xs mb-1" data-id="element-1345" data-mp="2cgdn5rhhejpu">
                                      <span className="text-gray-400" data-id="element-1346" data-mp="2hf6vz6ltpmjo">
                                        Liked Packaging
                                      </span>
                                      <span className="text-white font-bold" data-id="element-1347" data-mp="27koodqdad6ul">
                                        {packPct}%
                                      </span>
                                    </div>
                                    <div className="w-full bg-gray-800 h-1.5 rounded-full overflow-hidden" data-id="element-1348" data-mp="1yip2ajnezcks">
                                      <div className="bg-blue-400 h-full" style={{
                            width: `${packPct}%`
                          }} data-id="element-1349" data-mp="3nvakdontkf8a"></div>
                                    </div>
                                  </div>
                                  <div data-id="element-1350" data-mp="vjljg4rsg3s3">
                                    <div className="flex justify-between text-xs mb-1" data-id="element-1351" data-mp="rkmkd0sqn18k">
                                      <span className="text-gray-400" data-id="element-1352" data-mp="mkqh53rv7du2">
                                        Would Buy Again
                                      </span>
                                      <span className="text-white font-bold" data-id="element-1353" data-mp="hnzx65cxvy3d">
                                        {buyPct}%
                                      </span>
                                    </div>
                                    <div className="w-full bg-gray-800 h-1.5 rounded-full overflow-hidden" data-id="element-1354" data-mp="270v1qnz6kprt">
                                      <div className="bg-purple-400 h-full" style={{
                            width: `${buyPct}%`
                          }} data-id="element-1355" data-mp="22zjbqhcea2j3"></div>
                                    </div>
                                  </div>
                                </div>
                              </div>;
                })}
                      </div>
                    </div>;
          })}

                <h3 className="text-md font-bold mt-8 mb-4" data-id="element-1356" data-mp="36wlnxvgkd4r3">
                  Recent Reviews
                  {activeBrand && <span className="text-gray-400 font-normal" data-id="element-1357" data-mp="3ax5jiulho0b4">
                      {' '}
                      — {activeBrand}
                    </span>}
                </h3>
                <div className="overflow-x-auto -mx-6" data-id="element-1358" data-mp="q7ommnvrma0h">
                  <table className="w-full text-sm" data-id="element-1359" data-mp="v7axiqo4fe64">
                    <thead data-id="element-1360" data-mp="v5q26ytzfr77">
                      <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-800" data-id="element-1361" data-mp="sk7tmpemplib">
                        <th className="px-6 py-3" data-id="element-1362" data-mp="xisw6vhcsh9x">User</th>
                        <th className="px-4 py-3" data-id="element-1363" data-mp="xlf2yswf6sr3">Product</th>
                        <th className="px-4 py-3" data-id="element-1364" data-mp="11juu98zjanab">Brand</th>
                        <th className="px-4 py-3" data-id="element-1365" data-mp="16jzbj189nm1l">Taste</th>
                        <th className="px-4 py-3" data-id="element-1366" data-mp="16lk62qukz4zg">Packaging</th>
                        <th className="px-4 py-3" data-id="element-1367" data-mp="1bjvhv74frpkb">Buy Again</th>
                        <th className="px-4 py-3" data-id="element-1368" data-mp="1bh99545t740i">Date</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-1369" data-mp="1l95catmr5kbl">
                      {visibleReviews.slice().reverse().map(r => <tr key={r.id} className="border-b border-gray-800/50 hover:bg-gray-900/50" data-id="element-1370" data-mp="1pcbenq6qurcs">
                            <td className="px-6 py-3 text-gray-400 font-mono text-xs" data-id="element-1371" data-mp="1z8y1gkimrshg">
                              {r.userId}
                            </td>
                            <td className="px-4 py-3 font-medium text-white" data-id="element-1372" data-mp="24cgwetjpu1g2">
                              {r.productName}
                            </td>
                            <td className="px-4 py-3 text-gray-400" data-id="element-1373" data-mp="1zg8jlmszqkc2">
                              {r.productBrand}
                            </td>
                            <td className="px-4 py-3" data-id="element-1374" data-mp="2s21jnomwjkv5">
                              {r.taste === null ? '—' : r.taste ? <span className="text-green-400" data-id="element-1375" data-mp="2ic84yergqpmo">👍</span> : <span className="text-red-400" data-id="element-1376" data-mp="2eawnwxmnncq3">👎</span>}
                            </td>
                            <td className="px-4 py-3" data-id="element-1377" data-mp="3avotal67mc15">
                              {r.packaging === null ? '—' : r.packaging ? <span className="text-green-400" data-id="element-1378" data-mp="147eq37clhic5">👍</span> : <span className="text-red-400" data-id="element-1379" data-mp="ufihh2cvu9to">👎</span>}
                            </td>
                            <td className="px-4 py-3" data-id="element-1380" data-mp="zdtqjavcsgau">
                              {r.buyAgain === null ? '—' : r.buyAgain ? <span className="text-green-400" data-id="element-1381" data-mp="qe6znj092sw1">👍</span> : <span className="text-red-400" data-id="element-1382" data-mp="bhppgv3srkb2">👎</span>}
                            </td>
                            <td className="px-4 py-3 text-gray-500 text-xs" data-id="element-1383" data-mp="ghtwzvmq94p5">
                              {new Date(r.timestamp).toLocaleDateString()}
                            </td>
                          </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>;
      })()}

        {/* DROPS TAB */}
        {tab === 'drops' && <div className="space-y-6" data-id="element-1384" data-mp="3qavgtowkenf0">
            {/* Persistence failure — writes are being rejected by the DB */}
            {dropWriteError && <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-4 flex items-start gap-3" data-id="element-1385" data-mp="44q5v4i6f13n">
                <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" data-id="element-1386" data-mp="hvbqoyr6xl46" />
                <div className="text-sm flex-1" data-id="element-1387" data-mp="du9qaeljf30m">
                  <p className="font-bold text-red-300" data-id="element-1388" data-mp="dxnrvkmdn870">
                    Drops aren’t saving to the database
                  </p>
                  <p className="text-gray-300 mt-1" data-id="element-1389" data-mp="mvhkolkxnjpf">{dropWriteError}</p>
                  <p className="text-gray-500 mt-2 text-xs" data-id="element-1390" data-mp="2tmfqvrpjmc1d">
                    Fix: open{' '}
                    <span className="font-mono text-gray-400" data-id="element-1391" data-mp="2oncji0m3btq8">
                      lib/supabase.ts
                    </span>{' '}
                    and run the ROW LEVEL SECURITY block in your Supabase SQL
                    editor (safe to re-run), or disable RLS on the tables. Then
                    reload.
                  </p>
                </div>
                <button onClick={clearDropWriteError} className="p-1.5 rounded-full hover:bg-red-500/20 text-red-300 shrink-0" aria-label="Dismiss" data-id="element-1392" data-mp="3038vh9m84mma">
                  <X className="w-4 h-4" data-id="element-1393" data-mp="27bxkv5epvrpo" />
                </button>
              </div>}
            {/* Sync status / one-time sheet setup */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start space-x-3" data-id="element-1394" data-mp="2hby995rg0y0q">
              <RefreshCw className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" data-id="element-1395" data-mp="2m3pxvar1gto8" />
              <div className="text-sm" data-id="element-1396" data-mp="1ooc4vvae5pct">
                <p className="font-bold text-blue-300" data-id="element-1397" data-mp="1onahcrc6vae7">
                  Drops sync to every device
                </p>
                <p className="text-gray-400 mt-1" data-id="element-1398" data-mp="1tkan1154mb4s">
                  Creating, editing, or deleting a drop here writes it to
                  Supabase, so it pops up for everyone using the app on any
                  device within ~30 seconds.
                </p>
                <p className="text-gray-500 mt-2 text-xs" data-id="element-1399" data-mp="1p9kjgr3jyn4">
                  One-time setup: run the schema SQL from{' '}
                  <span className="font-mono text-gray-400" data-id="element-1400" data-mp="3sz8atzuiqznd">
                    lib/supabase.ts
                  </span>{' '}
                  in your Supabase SQL editor to create the{' '}
                  <span className="font-mono text-gray-400" data-id="element-1401" data-mp="3j57ybcwwqs4m">
                    users, catches, reviews, drops, app_config
                  </span>{' '}
                  tables. After that, everything syncs automatically.
                </p>
              </div>
            </div>

            {/* Smart Drop Engine — recognise a product from a photo + website */}
            <SmartDropEngine onApply={applyRecognition} data-id="element-1402" data-mp="309hwbpoz5vqq" />

            <div className="flex justify-between items-center" data-id="element-1403" data-mp="2qkuh2c3vxnxe">
              <h2 className="text-lg font-bold" data-id="element-1404" data-mp="2qjjc48ypea4p">Upcoming Drops</h2>
              <Button onClick={() => setShowAddDrop(!showAddDrop)} className="text-sm py-1.5 px-3 flex items-center" data-id="element-1405" data-mp="2vk7ajfp9ht8y">
                {showAddDrop ? 'Cancel' : <>
                    <Plus className="w-4 h-4 mr-1" data-id="element-1406" data-mp="39blxtyq7cglt" /> Add Drop
                  </>}
              </Button>
            </div>

            {/* Recurring Questions Editor */}
            <div className="bg-gray-900 border border-gray-800 p-5 rounded-xl" data-id="element-1407" data-mp="3ec9pmmm991h3">
              <div className="flex justify-between items-center mb-4" data-id="element-1408" data-mp="30gxsy9ojh0xj">
                <div data-id="element-1409" data-mp="34javyqbwikqm">
                  <h3 className="font-bold text-white text-lg" data-id="element-1410" data-mp="34isp6zrm4o4g">
                    Recurring User Questions
                  </h3>
                  <p className="text-sm text-gray-400" data-id="element-1411" data-mp="vabbvjepcjn">
                    Asked to users on their 4th catch or later (Max 3).
                  </p>
                </div>
                <Button variant="secondary" onClick={() => setShowRecurringEditor(!showRecurringEditor)} data-id="element-1412" data-mp="1ljuvkmx8m4ia">
                  {showRecurringEditor ? 'Close' : 'Edit'}
                </Button>
              </div>
              {showRecurringEditor && <div className="mt-4 pt-4 border-t border-gray-800" data-id="element-1413" data-mp="1qeqwydod6482">
                  <QuestionEditor questions={recurringQuestions} onChange={setRecurringQuestions} max={3} data-id="element-1414" data-mp="12nv3lc49f0yp" />
                </div>}
            </div>

            {/* DROP CODE LOOKUP */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800" data-id="element-1415" data-mp="17lws4ckgfshk">
              <h3 className="font-bold mb-1 flex items-center" data-id="element-1416" data-mp="2o06amq20mn0h">
                <Ticket className="w-5 h-5 mr-2 text-brand-yellow" data-id="element-1417" data-mp="2o2iwvfet3jjc" />
                Look up a drop by code
              </h3>
              <p className="text-xs text-gray-500 mb-4" data-id="element-1418" data-mp="gexzx1lzp3vf">
                Enter the code a drop was named with to see its exact date,
                brand and everyone who joined.
              </p>
              <Input placeholder="e.g. DROP-7K2M" value={lookupCode} onChange={e => setLookupCode(e.target.value)} className="bg-gray-800 border-gray-700 text-white font-mono uppercase" data-id="element-1419" data-mp="qd411gzz25z8" />

              {lookupCode.trim().length > 0 && !lookedUpDrop && <p className="text-sm text-red-400 mt-4" data-id="element-1420" data-mp="1437dxhcyonc0">
                  No drop found with code “{lookupCode.trim().toUpperCase()}”.
                </p>}

              {lookedUpDrop && (() => {
            const participants = participantsForDrop(lookedUpDrop);
            return <div className="mt-5 space-y-5" data-id="element-1421" data-mp="3awt6qocy373b">
                      <div className="bg-gray-950 rounded-xl p-5 border border-gray-800" data-id="element-1422" data-mp="3fuun31izksb3">
                        <div className="flex items-center justify-between mb-4" data-id="element-1423" data-mp="38aimt0d5qr64">
                          <div data-id="element-1424" data-mp="33jtuxjxkfs6g">
                            <h4 className="text-xl font-bold text-white" data-id="element-1425" data-mp="33iiqrj07kiet">
                              {lookedUpDrop.title}
                            </h4>
                            <span className="font-mono text-xs bg-brand-yellow/20 text-brand-yellow px-2 py-0.5 rounded" data-id="element-1426" data-mp="2tle1hab2g6ts">
                              {lookedUpDrop.code}
                            </span>
                          </div>
                          <span className="bg-blue-500/20 text-blue-400 text-xs px-2 py-1 rounded font-bold whitespace-nowrap" data-id="element-1427" data-mp="3mb38c4agj13b">
                            {lookedUpDrop.boxesLeft} / {lookedUpDrop.totalBoxes}{' '}
                            left
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm" data-id="element-1428" data-mp="25wl4daipjsxl">
                          <div className="flex items-center text-gray-300" data-id="element-1429" data-mp="20vnm6xxp6yw3">
                            <Tag className="w-4 h-4 mr-2 text-gray-500 shrink-0" data-id="element-1430" data-mp="z4j9tybjckai" />
                            <span data-id="element-1431" data-mp="u5zifie6agar">{lookedUpDrop.brand}</span>
                          </div>
                          <div className="flex items-center text-gray-300" data-id="element-1432" data-mp="18uvppd8enpab">
                            <Calendar className="w-4 h-4 mr-2 text-gray-500 shrink-0" data-id="element-1433" data-mp="18wyqmsb3j2tt" />
                            <span data-id="element-1434" data-mp="142ko85y0nvfn">{lookedUpDrop.date}</span>
                          </div>
                          <div className="flex items-center text-gray-300" data-id="element-1435" data-mp="1htz9ykjb6aqe">
                            <Clock className="w-4 h-4 mr-2 text-gray-500 shrink-0" data-id="element-1436" data-mp="1cx8y69eje1as" />
                            <span data-id="element-1437" data-mp="1ru9n655cfi5k">{lookedUpDrop.time}</span>
                          </div>
                          <div className="flex items-center text-gray-300 sm:col-span-3" data-id="element-1438" data-mp="1muwswzc578sy">
                            <MapPin className="w-4 h-4 mr-2 text-gray-500 shrink-0" data-id="element-1439" data-mp="3tl35v1amofhs" />
                            <span data-id="element-1440" data-mp="3tmxtindaalpk">{lookedUpDrop.address}</span>
                          </div>
                        </div>
                      </div>

                      <div data-id="element-1441" data-mp="2450qw3ldaj6m">
                        <div className="flex items-center justify-between mb-3" data-id="element-1442" data-mp="244r5ktb9p079">
                          <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-1443" data-mp="292iz1rrk8udk">
                            Participants
                          </h5>
                          <span className="text-xs font-bold text-white" data-id="element-1444" data-mp="1zch90ufnd1w0">
                            {participants.length} joined
                          </span>
                        </div>
                        {participants.length === 0 ? <div className="bg-gray-950 rounded-xl p-6 border border-gray-800 text-center text-gray-500 text-sm" data-id="element-1445" data-mp="1pcytqraom4aa">
                            No one has joined this drop yet.
                          </div> : <div className="overflow-x-auto -mx-6" data-id="element-1446" data-mp="35t1qb556qlb8">
                            <table className="w-full text-sm" data-id="element-1447" data-mp="3ase9nfwula92">
                              <thead data-id="element-1448" data-mp="3armrb6s9h90q">
                                <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-800" data-id="element-1449" data-mp="39az6kivlzgnc">
                                  <th className="px-6 py-3" data-id="element-1450" data-mp="34b4fae7x8p1r">Name</th>
                                  <th className="px-4 py-3" data-id="element-1451" data-mp="2zct5g3hmmejj">Phone</th>
                                  <th className="px-4 py-3" data-id="element-1452" data-mp="2zf5mmefk8kvj">Code</th>
                                  <th className="px-4 py-3" data-id="element-1453" data-mp="2uf9r73kxi1rl">Status</th>
                                  <th className="px-4 py-3" data-id="element-1454" data-mp="3s29tyq24lbah">Joined</th>
                                </tr>
                              </thead>
                              <tbody data-id="element-1455" data-mp="3n3fdvvvjm0u2">
                                {participants.map((p, i) => <tr key={`${p.code}-${i}`} className="border-b border-gray-800/50 hover:bg-gray-900/50" data-id="element-1456" data-mp="3ia469siwpg13" data-mpi={i}>
                                    <td className="px-6 py-3 font-medium text-white whitespace-nowrap" data-id="element-1457" data-mp="9w3x6v70ist3">
                                      {p.name}
                                    </td>
                                    <td className="px-4 py-3 text-gray-400 font-mono text-xs" data-id="element-1458" data-mp="m6d1xp8zp1ij">
                                      {p.phone}
                                    </td>
                                    <td className="px-4 py-3 text-gray-400 font-mono text-xs" data-id="element-1459" data-mp="r7iy5x1uz6wt">
                                      {p.code}
                                    </td>
                                    <td className="px-4 py-3" data-id="element-1460" data-mp="11341byinyd85">
                                      <span className="text-[10px] font-bold px-2 py-1 rounded bg-gray-800 text-gray-300 uppercase tracking-wider" data-id="element-1461" data-mp="154fj5hvapu5s">
                                        {p.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-500 text-xs" data-id="element-1462" data-mp="1judau7sfp1zq">
                                      {new Date(p.when).toLocaleDateString()}
                                    </td>
                                  </tr>)}
                              </tbody>
                            </table>
                          </div>}
                      </div>
                    </div>;
          })()}
            </div>

            {showAddDrop && <div id="add-drop-form" className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-6 scroll-mt-6" data-id="element-1463" data-mp="2qbtp6jtdlt16">
                <h3 className="font-bold mb-4" data-id="element-1464" data-mp="1xn616gh8spjx">Add New Product Drop</h3>
                {addDropError && <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300" data-id="element-1465" data-mp="2bmnossisxwrp">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" data-id="element-1466" data-mp="27mwu4dq5mwhw" />
                    {addDropError}
                  </div>}
                <form onSubmit={handleAddDrop} className="space-y-4" data-id="element-1467" data-mp="3j0qulmchnvci">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1468" data-mp="3qji0pyzx0fk5">
                    <div data-id="element-1469" data-mp="3qlkzb7f4vh0h">
                      <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1470" data-mp="3vil1v8cry1mu">
                        Product Title
                      </label>
                      <Input required placeholder="e.g. Summer Refresh Drop" value={newDrop.title} onChange={e => setNewDrop({
                  ...newDrop,
                  title: e.target.value
                })} className="bg-gray-800 border-gray-700 text-white" data-id="element-1471" data-mp="3gn57114de1pe" />
                    </div>
                    <div data-id="element-1472" data-mp="8bitedr2isph">
                      <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1473" data-mp="w2xz8gi1zs5h">
                        Brand
                      </label>
                      <Input required placeholder="e.g. NOCCO" value={newDrop.brand} onChange={e => setNewDrop({
                  ...newDrop,
                  brand: e.target.value
                })} className="bg-gray-800 border-gray-700 text-white" data-id="element-1474" data-mp="10vherj0awxcx" />
                    </div>
                    <div data-id="element-1475" data-mp="rtdltyd53ktg">
                      <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1476" data-mp="n0u6avuw6fm0">
                        Campaign{' '}
                        <span className="text-gray-600 normal-case font-normal" data-id="element-1477" data-mp="i2akqwk7n5u6">
                          (optional)
                        </span>
                      </label>
                      <Input placeholder="Defaults to the product title" value={newDrop.campaign} onChange={e => setNewDrop({
                  ...newDrop,
                  campaign: e.target.value
                })} className="bg-gray-800 border-gray-700 text-white" data-id="element-1478" data-mp="91ch4fdop0c4" />
                    </div>
                    <div data-id="element-1479" data-mp="2tpcm00gpwclo">
                      <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1480" data-mp="2tnjjmxd50ejh">
                        Verification Method
                      </label>
                      <select required value={newDrop.verificationMethod} onChange={e => setNewDrop({
                  ...newDrop,
                  verificationMethod: e.target.value as VerificationMethod
                })} className="w-full rounded-lg bg-gray-800 border border-gray-700 text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue" data-id="element-1481" data-mp="21x599b4c1kck">
                        {VERIFICATION_METHODS.map(m => <option key={m.value} value={m.value} data-id="element-1482" data-mp="lkjblnlj6qvl">
                            {m.label}
                          </option>)}
                      </select>
                    </div>
                    <div className="md:col-span-2" data-id="element-1483" data-mp="ukpl6ye3z6ph">
                      <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1484" data-mp="ujejd4484910">
                        Description{' '}
                        <span className="text-gray-600 normal-case font-normal" data-id="element-1485" data-mp="14aratum40x6i">
                          (optional)
                        </span>
                      </label>
                      <Input placeholder="Short description of the product" value={newDrop.description} onChange={e => setNewDrop({
                  ...newDrop,
                  description: e.target.value
                })} className="bg-gray-800 border-gray-700 text-white" data-id="element-1486" data-mp="zea9tzttaoc0" />
                    </div>
                    <div className="md:col-span-2" data-id="element-1487" data-mp="1nwsta6443xa7">
                      <label className="text-xs text-gray-500 uppercase font-bold mb-2 block" data-id="element-1488" data-mp="1nvhoc2yxkjhi">
                        Availability
                      </label>
                      <AvailabilityEditor value={newDropAvailability} onChange={setNewDropAvailability} data-id="element-1489" data-mp="1dyd3q7kqgw2t" />
                    </div>
                    <div data-id="element-1490" data-mp="148jzjrmx7j8l">
                      <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1491" data-mp="149v4hus3qx1a">
                        Location
                      </label>
                      <AddressAutocomplete value={newDrop.address} onChange={v => setNewDrop({
                  ...newDrop,
                  address: v
                })} placeholder="Start typing an address…" data-id="element-1492" data-mp="v85conub5w2z" />
                      <p className="text-[11px] text-gray-500 mt-1" data-id="element-1493" data-mp="pdn8zpr5immj">
                        Pick a suggestion so the drop shows a correct, mappable
                        address.
                      </p>
                    </div>
                    <div data-id="element-1494" data-mp="1i2lu8vqkm4w2">
                      <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1495" data-mp="1i1svagxusjoh">
                        Amount (Total Boxes)
                      </label>
                      <Input required type="number" min="1" value={newDrop.totalBoxes} onChange={e => setNewDrop({
                  ...newDrop,
                  totalBoxes: Number(e.target.value)
                })} className="bg-gray-800 border-gray-700 text-white" data-id="element-1496" data-mp="18br6to1l8z91" />
                    </div>
                    <div className="md:col-span-2 border-t border-gray-800 pt-4 mt-2" data-id="element-1497" data-mp="3udnat25hjpol">
                      <h4 className="text-sm font-bold text-white mb-3" data-id="element-1498" data-mp="3qah4xvmac0in">
                        Brand Assets (Optional)
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1499" data-mp="2xljsi94r8xxt">
                        <div data-id="element-1500" data-mp="2xnmmf4913b7t">
                          <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1501" data-mp="3blbasm2b10hm">
                            Logo URL
                          </label>
                          <Input placeholder="https://..." value={newDrop.brandLogo} onChange={e => setNewDrop({
                      ...newDrop,
                      brandLogo: e.target.value
                    })} className="bg-gray-800 border-gray-700 text-white" data-id="element-1502" data-mp="qutzyx43dey4" />
                        </div>
                        <div data-id="element-1503" data-mp="3tnqgu33fv34b">
                          <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1504" data-mp="3tmylmtdr77ek">
                            Instagram URL
                          </label>
                          <Input placeholder="https://instagram.com/..." value={newDrop.instagramUrl} onChange={e => setNewDrop({
                      ...newDrop,
                      instagramUrl: e.target.value
                    })} className="bg-gray-800 border-gray-700 text-white" data-id="element-1505" data-mp="30zumv6597pb8" />
                        </div>
                        <div data-id="element-1506" data-mp="2amg61fb4qd65">
                          <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1507" data-mp="25p7mpbe8w33b">
                            Website URL
                          </label>
                          <Input placeholder="https://..." value={newDrop.websiteUrl} onChange={e => setNewDrop({
                      ...newDrop,
                      websiteUrl: e.target.value
                    })} className="bg-gray-800 border-gray-700 text-white" data-id="element-1508" data-mp="20sh34e38epd5" />
                        </div>
                      </div>
                    </div>

                    <div className="md:col-span-2 border-t border-gray-800 pt-4 mt-2" data-id="element-1509" data-mp="191jcdfkfdeub">
                      <h4 className="text-sm font-bold text-white mb-1" data-id="element-1510" data-mp="1415f8skd3pb2">
                        Required Level &amp; Audience (Optional)
                      </h4>
                      <p className="text-xs text-gray-500 mb-3" data-id="element-1511" data-mp="3835dac5fvtuu">
                        Choose who can see this Drop. Empty filters = everyone.
                        The brand logo auto-fills from the brand name when left
                        blank.
                      </p>
                      <div className="space-y-4" data-id="element-1512" data-mp="2ke4b36n79ey0">
                        <div data-id="element-1513" data-mp="2kdb2s18lehdr">
                          <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1514" data-mp="2pdp8vdqmvh95">
                            Required level
                          </label>
                          <select value={newDrop.requiredLevel} onChange={e => setNewDrop({
                      ...newDrop,
                      requiredLevel: e.target.value
                    })} className="w-full rounded-lg bg-gray-800 border border-gray-700 text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue" data-id="element-1515" data-mp="2bg0soj7igyaf">
                            {LEVEL_NAMES.map(lvl => <option key={lvl} value={lvl} data-id="element-1516" data-mp="xjqyg4lnxd8z">
                                {lvl}
                              </option>)}
                          </select>
                        </div>
                        <div data-id="element-1517" data-mp="12hsj2tyqlkmk">
                          <label className="text-xs text-gray-500 uppercase font-bold mb-1.5 block" data-id="element-1518" data-mp="12ghfov97eevz">
                            Age ranges
                          </label>
                          <ChipMultiSelect options={AGE_OPTIONS.map(v => ({
                      value: v,
                      label: v
                    }))} selected={newDropTargeting.ageRanges || []} onToggle={v => setNewDropTargeting(t => ({
                      ...t,
                      ageRanges: (t.ageRanges || []).includes(v) ? (t.ageRanges || []).filter(x => x !== v) : [...(t.ageRanges || []), v]
                    }))} data-id="element-1519" data-mp="1cex58tsl1g3d" />
                        </div>
                        <div data-id="element-1520" data-mp="253l8st3siedn">
                          <label className="text-xs text-gray-500 uppercase font-bold mb-1.5 block" data-id="element-1521" data-mp="252tdlje3uinw">
                            Interests
                          </label>
                          <ChipMultiSelect options={INTEREST_OPTIONS.map(v => ({
                      value: v,
                      label: v
                    }))} selected={newDropTargeting.interests || []} onToggle={v => setNewDropTargeting(t => ({
                      ...t,
                      interests: (t.interests || []).includes(v) ? (t.interests || []).filter(x => x !== v) : [...(t.interests || []), v]
                    }))} data-id="element-1522" data-mp="2etehnmcjsfe7" />
                        </div>
                        <div data-id="element-1523" data-mp="vwt5zmnxk653">
                          <label className="text-xs text-gray-500 uppercase font-bold mb-1.5 block" data-id="element-1524" data-mp="qvn7t9bi3qo6">
                            Occupation
                          </label>
                          <ChipMultiSelect options={OCCUPATION_OPTIONS.map(v => ({
                      value: v,
                      label: v
                    }))} selected={newDropTargeting.occupations || []} onToggle={v => setNewDropTargeting(t => ({
                      ...t,
                      occupations: (t.occupations || []).includes(v) ? (t.occupations || []).filter(x => x !== v) : [...(t.occupations || []), v]
                    }))} data-id="element-1525" data-mp="h63ns0c1xqrq" />
                        </div>
                        <div data-id="element-1526" data-mp="10vuxb5rz5opf">
                          <label className="text-xs text-gray-500 uppercase font-bold mb-1.5 block" data-id="element-1527" data-mp="15x0upgwky05a">
                            Location (city)
                          </label>
                          <ChipMultiSelect options={CITY_OPTIONS.map(v => ({
                      value: v,
                      label: v
                    }))} selected={newDropTargeting.cities || []} onToggle={v => setNewDropTargeting(t => ({
                      ...t,
                      cities: (t.cities || []).includes(v) ? (t.cities || []).filter(x => x !== v) : [...(t.cities || []), v]
                    }))} data-id="element-1528" data-mp="wvuaoxuq277a" />
                        </div>
                        <div data-id="element-1529" data-mp="2qf2ztea7ueu7">
                          <label className="text-xs text-gray-500 uppercase font-bold mb-1.5 block" data-id="element-1530" data-mp="2lfqewz2wnhu9">
                            Activity level
                          </label>
                          <ChipMultiSelect options={ACTIVITY_OPTIONS} selected={newDropTargeting.activityLevels || []} onToggle={v => setNewDropTargeting(t => {
                      const cur = t.activityLevels || [];
                      const next = cur.includes(v as 'new' | 'returning' | 'loyal') ? cur.filter(x => x !== v) : [...cur, v as 'new' | 'returning' | 'loyal'];
                      return {
                        ...t,
                        activityLevels: next
                      };
                    })} data-id="element-1531" data-mp="306pifvovvg6z" />
                        </div>
                      </div>
                    </div>

                    <div className="md:col-span-2 border-t border-gray-800 pt-4 mt-2" data-id="element-1532" data-mp="19rzel1t4dw2f">
                      <h4 className="text-sm font-bold text-white mb-1" data-id="element-1533" data-mp="15nrqn6uhkk59">
                        Products in Catch (Optional)
                      </h4>
                      <p className="text-xs text-gray-500 mb-3" data-id="element-1534" data-mp="3qe0l2txdai8t">
                        Add up to 4 products that users will review for this
                        drop.
                      </p>
                      <ProductEditor products={newDropProducts} onChange={setNewDropProducts} max={4} data-id="element-1535" data-mp="29xl0hy2f4rh7" />
                    </div>

                    <div className="md:col-span-2 border-t border-gray-800 pt-4 mt-2" data-id="element-1536" data-mp="2joxqsl8kj9l4">
                      <h4 className="text-sm font-bold text-white mb-1" data-id="element-1537" data-mp="2ons97m2miw4p">
                        Catch Questions (Optional)
                      </h4>
                      <p className="text-xs text-gray-500 mb-3" data-id="element-1538" data-mp="2to6gv30bc427">
                        Asked to everyone who catches this drop. Saved together
                        with the drop when you press Save.
                      </p>
                      <QuestionEditor questions={newDropQuestions} onChange={setNewDropQuestions} max={5} data-id="element-1539" data-mp="139waqrduo79a" />
                    </div>
                  </div>
                  <Button type="submit" className="w-full mt-4" data-id="element-1540" data-mp="2bjwr2azn8fd0">
                    Save Drop{' '}
                    {newDropQuestions.length > 0 && `+ ${newDropQuestions.length} question${newDropQuestions.length > 1 ? 's' : ''}`}
                  </Button>
                </form>
              </div>}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1541" data-mp="2uiipfi3rzsd8">
              {events.map(e => <div key={e.id} className="bg-gray-900 border border-gray-800 p-5 rounded-xl flex flex-col" data-id="element-1542" data-mp="2pk7ipg8s1xz8">
                  <div className="flex justify-between items-start mb-3" data-id="element-1543" data-mp="33cnd5snm4noj">
                    <div data-id="element-1544" data-mp="2za0twg50cb2y">
                      <h3 className="font-bold text-white text-lg" data-id="element-1545" data-mp="2zblfhg9cgjoo">
                        {e.title}
                      </h3>
                      <p className="text-sm text-blue-400 font-medium" data-id="element-1546" data-mp="38afetee27o5x">
                        {e.brand}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-1547" data-mp="3rw8nw7z5e136">
                        Campaign:{' '}
                        <span className="text-gray-300" data-id="element-1548" data-mp="ompatracot6">
                          {e.campaign || e.title}
                        </span>
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5" data-id="element-1549" data-mp="3mvba07l67t7h">
                      <span className="bg-blue-500/20 text-blue-400 text-xs px-2 py-1 rounded font-bold whitespace-nowrap" data-id="element-1550" data-mp="394xyjv92lwde">
                        {e.boxesLeft} / {e.totalBoxes} left
                      </span>
                      <span className="bg-brand-yellow/15 text-brand-yellow text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wide whitespace-nowrap" data-id="element-1551" data-mp="2z830fdgplxzn">
                        {VERIFICATION_METHODS.find(m => m.value === normalizeVerificationMethod(e.verificationMethod))?.label}
                      </span>
                    </div>
                  </div>

                  {/* One-click availability toggle (ON = live / OFF = hidden) */}
                  <button type="button" onClick={() => {
              updateEvent(e.id, {
                enabled: !isDropEnabled(e)
              });
              flashSaved(e.id);
            }} aria-pressed={isDropEnabled(e)} className={`w-full flex items-center justify-between rounded-lg border px-3 py-2 mb-3 transition-colors ${isDropEnabled(e) ? 'bg-green-500/10 border-green-500/40 hover:bg-green-500/20' : 'bg-gray-800 border-gray-700 hover:bg-gray-750'}`} data-id="element-1552" data-mp="2ddct77esx7j8">
                    <span className="flex items-center gap-2" data-id="element-1553" data-mp="3juhtfw5n0qqm">
                      <Power className={`w-4 h-4 ${isDropEnabled(e) ? 'text-green-400' : 'text-gray-500'}`} data-id="element-1554" data-mp="3jswvrxo10roj" />
                      <span className={`text-xs font-bold uppercase tracking-wide ${isDropEnabled(e) ? 'text-green-400' : 'text-gray-400'}`} data-id="element-1555" data-mp="765jnxhwla7">
                        {isDropEnabled(e) ? 'Available — ON' : 'Turned OFF'}
                      </span>
                    </span>
                    <span className={`relative w-10 h-6 rounded-full transition-colors ${isDropEnabled(e) ? 'bg-green-500' : 'bg-gray-600'}`} data-id="element-1556" data-mp="39eqou21zuexo">
                      <span className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-all ${isDropEnabled(e) ? 'left-5' : 'left-1'}`} data-id="element-1557" data-mp="3id2p6vsnwr3b" />
                    </span>
                  </button>
                  {/* Drop code — the name used to look this drop up */}
                  <button onClick={() => copyCode(e.code)} className="flex items-center justify-between bg-brand-yellow/10 hover:bg-brand-yellow/20 border border-brand-yellow/30 rounded-lg px-3 py-2 mb-3 transition-colors group" title="Copy drop code" data-id="element-1558" data-mp="12kv9xik3685y">
                    <span className="font-mono text-sm font-bold text-brand-yellow" data-id="element-1559" data-mp="hxnkdjt78q48">
                      {e.code}
                    </span>
                    {copiedCode === e.code ? <CheckCircle className="w-4 h-4 text-green-400" data-id="element-1560" data-mp="3qb4ffbikd93h" /> : <Copy className="w-4 h-4 text-brand-yellow/70 group-hover:text-brand-yellow" data-id="element-1561" data-mp="3lcjdn7nnyhcx" />}
                  </button>
                  <p className="text-sm text-gray-400 mb-4 flex-grow" data-id="element-1562" data-mp="3vbijyjkyi0wr">
                    {e.description}
                  </p>
                  <div className="bg-gray-950 rounded-lg p-3 text-xs text-gray-500 space-y-2 border border-gray-800" data-id="element-1563" data-mp="37l4tuylxmc4t">
                    <div className="flex justify-between" data-id="element-1564" data-mp="32m1u9toq0y48">
                      <span data-id="element-1565" data-mp="1i528nj62vjl7">When:</span>
                      <span className="text-gray-300" data-id="element-1566" data-mp="1i40wfbdbsr1w">
                        {!e.date ? 'Available now' : e.time ? `${e.date} ${e.time}` : `${e.date} · all day`}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-1567" data-mp="20vtpf5seac9m">
                      <span data-id="element-1568" data-mp="20u8q72v4y55f">Location:</span>
                      <span className="text-gray-300" data-id="element-1569" data-mp="25vod70gvilqg">{e.address}</span>
                    </div>
                    <div className="flex justify-between" data-id="element-1570" data-mp="1rwexs30hrvnj">
                      <span data-id="element-1571" data-mp="1rxzsbsmt3ele">Repeats:</span>
                      <span className="text-gray-300" data-id="element-1572" data-mp="1vwh08ee1wt3v">
                        {describeRecurrence(normalizeRecurrence(e.recurrence))}
                        {isRecurring(normalizeRecurrence(e.recurrence)) && nextOccurrence(e) && ` · next ${nextOccurrence(e)}`}
                      </span>
                    </div>
                  </div>

                  {/* Real-time redeemed counter (syncs via boxesLeft) */}
                  <div className="mt-3 bg-gray-950 rounded-lg p-3 border border-gray-800" data-id="element-1573" data-mp="134ymk8j8kwef">
                    <div className="flex items-center justify-between text-xs mb-1.5" data-id="element-1574" data-mp="136t5jhay6efv">
                      <span className="text-gray-500 uppercase font-bold tracking-wider" data-id="element-1575" data-mp="y7ygvyqatvnu">
                        Redeemed
                      </span>
                      <span className="font-bold text-white" data-id="element-1576" data-mp="1quau73yx0wem">
                        {redeemedCount(e)} / {e.totalBoxes}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-800 overflow-hidden" data-id="element-1577" data-mp="1h4zj5bsgxho3">
                      <div className="h-full bg-brand-blue rounded-full transition-all duration-500" style={{
                  width: `${e.totalBoxes > 0 ? Math.min(100, Math.round(redeemedCount(e) / e.totalBoxes * 100)) : 0}%`
                }} data-id="element-1578" data-mp="2ek52r9c7byt2" />
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1.5" data-id="element-1579" data-mp="2m467ccae7t7v">
                      {e.boxesLeft} remaining
                    </p>
                  </div>

                  {/* Drop Questions Editor */}
                  <div className="mt-4" data-id="element-1580" data-mp="2v5njjjkojara">
                    <div className="flex items-center justify-between" data-id="element-1581" data-mp="303xj06dfsxip">
                      <button onClick={() => setEditingDropQs(editingDropQs === e.id ? null : e.id)} className="flex items-center text-sm text-blue-400 hover:text-blue-300" data-id="element-1582" data-mp="301l1fublcp66">
                        <Settings2 className="w-4 h-4 mr-1" data-id="element-1583" data-mp="3eu4shwlgv679" />
                        {editingDropQs === e.id ? 'Hide Questions & Assets' : `Edit Questions & Assets`}
                      </button>
                      {savedDropId === e.id && <span className="flex items-center text-xs text-green-400 font-medium" data-id="element-1584" data-mp="2g0urrcgc2swp">
                          <CheckCircle className="w-3.5 h-3.5 mr-1" data-id="element-1585" data-mp="2kzftxhf5bmns" /> Saved
                        </span>}
                    </div>
                    {editingDropQs === e.id && <div className="mt-3 pt-3 border-t border-gray-800 space-y-4" data-id="element-1586" data-mp="2tvx4ojjllij0">
                        <p className="text-[11px] text-gray-500 italic" data-id="element-1587" data-mp="1x5xpw7cutnrz">
                          Changes save automatically and sync to every device.
                        </p>
                        <div data-id="element-1588" data-mp="2675o6pfdeom2">
                          <h4 className="text-xs font-bold text-gray-400 uppercase mb-2" data-id="element-1589" data-mp="266vxezmi4dfb">
                            Details
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-1590" data-mp="1dj9sno1jf2qv">
                            <div className="sm:col-span-2" data-id="element-1591" data-mp="1ii2ugj7gp5b7">
                              <label className="text-[11px] text-gray-500 mb-1 block" data-id="element-1592" data-mp="1azlgo0sjz4cf">
                                Title
                              </label>
                              <Input value={e.title} onChange={ev => {
                        updateEvent(e.id, {
                          title: ev.target.value
                        });
                        flashSaved(e.id);
                      }} className="bg-gray-800 border-gray-700 text-white text-sm" data-id="element-1593" data-mp="1611t5vyb9khy" />
                            </div>
                            <div data-id="element-1594" data-mp="i8l9jgyxdkgj">
                              <label className="text-[11px] text-gray-500 mb-1 block" data-id="element-1595" data-mp="dcmh9iah7iau">
                                Brand
                              </label>
                              <Input value={e.brand} onChange={ev => {
                        updateEvent(e.id, {
                          brand: ev.target.value
                        });
                        flashSaved(e.id);
                      }} className="bg-gray-800 border-gray-700 text-white text-sm" data-id="element-1596" data-mp="9c2k4epwkeop" />
                            </div>
                            <div data-id="element-1597" data-mp="ddiodkcb5mh4">
                              <label className="text-[11px] text-gray-500 mb-1 block" data-id="element-1598" data-mp="dek80d7e3hag">
                                Campaign
                              </label>
                              <Input value={e.campaign || ''} placeholder="e.g. Summer Energy Drop" onChange={ev => {
                        updateEvent(e.id, {
                          campaign: ev.target.value
                        });
                        flashSaved(e.id);
                      }} className="bg-gray-800 border-gray-700 text-white text-sm" data-id="element-1599" data-mp="3p0pj8nl9pg4" />
                            </div>
                            <div className="sm:col-span-2" data-id="element-1600" data-mp="2ulajuarajwth">
                              <label className="text-[11px] text-gray-500 mb-1 block" data-id="element-1601" data-mp="2qkqjkybf8d34">
                                Description
                              </label>
                              <Input value={e.description} onChange={ev => {
                        updateEvent(e.id, {
                          description: ev.target.value
                        });
                        flashSaved(e.id);
                      }} className="bg-gray-800 border-gray-700 text-white text-sm" data-id="element-1602" data-mp="22vfsk8jvhx26" />
                            </div>
                            <div data-id="element-1603" data-mp="hgysc9nf85a6">
                              <label className="text-[11px] text-gray-500 mb-1 block" data-id="element-1604" data-mp="hi0bz2ii603i">
                                Date
                              </label>
                              <Input type="date" value={e.date} onChange={ev => {
                        updateEvent(e.id, {
                          date: ev.target.value
                        });
                        flashSaved(e.id);
                      }} className="bg-gray-800 border-gray-700 text-white text-sm" data-id="element-1605" data-mp="rf67cmy5gd9j" />
                            </div>
                            <div data-id="element-1606" data-mp="1f6bjos4vsnf3">
                              <label className="text-[11px] text-gray-500 mb-1 block" data-id="element-1607" data-mp="1k43kkbni7jvf">
                                Time
                              </label>
                              <Input placeholder="12:00 - 14:00" value={e.time} onChange={ev => {
                        updateEvent(e.id, {
                          time: ev.target.value
                        });
                        flashSaved(e.id);
                      }} className="bg-gray-800 border-gray-700 text-white text-sm" data-id="element-1608" data-mp="1o2swdzsw8tcy" />
                            </div>
                            <div className="sm:col-span-2" data-id="element-1609" data-mp="3ncvrduez5fbm">
                              <label className="text-[11px] text-gray-500 mb-1 block" data-id="element-1610" data-mp="3ikc4u7yb79uo">
                                Location
                              </label>
                              <AddressAutocomplete value={e.address} onChange={v => {
                        updateEvent(e.id, {
                          address: v
                        });
                        flashSaved(e.id);
                      }} placeholder="Start typing an address…" data-id="element-1611" data-mp="ewakcj5wkwb5" />
                            </div>
                            <div className="sm:col-span-2" data-id="element-1612" data-mp="1zm642y65c9v3">
                              <label className="text-[11px] text-gray-500 mb-1 block" data-id="element-1613" data-mp="1zkl8d583ikvn">
                                Verification Method
                              </label>
                              <select value={normalizeVerificationMethod(e.verificationMethod)} onChange={ev => {
                        updateEvent(e.id, {
                          verificationMethod: ev.target.value as VerificationMethod
                        });
                        flashSaved(e.id);
                      }} className="w-full rounded-lg bg-gray-800 border border-gray-700 text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue" data-id="element-1614" data-mp="1pq35216lcrcd">
                                {VERIFICATION_METHODS.map(m => <option key={m.value} value={m.value} data-id="element-1615" data-mp="7qwoplvzus28">
                                    {m.label}
                                  </option>)}
                              </select>
                            </div>
                          </div>
                        </div>
                        <div data-id="element-1616" data-mp="cpq4jkyr6v5k">
                          <h4 className="text-xs font-bold text-gray-400 uppercase mb-2" data-id="element-1617" data-mp="cqhotzmwh6gj">
                            Recurring Schedule
                          </h4>
                          <RecurrenceEditor recurrence={normalizeRecurrence(e.recurrence)} onChange={rec => {
                    updateEvent(e.id, {
                      recurrence: rec
                    });
                    flashSaved(e.id);
                  }} data-id="element-1618" data-mp="2ei4mgk1arzlz" />
                        </div>
                        <div data-id="element-1619" data-mp="3gswh35s0orvm">
                          <h4 className="text-xs font-bold text-gray-400 uppercase mb-2" data-id="element-1620" data-mp="2k3f383ivrziu">
                            Brand Assets
                          </h4>
                          <div className="space-y-2" data-id="element-1621" data-mp="2p31q74oigu4u">
                            <Input placeholder="Logo URL" value={e.brandLogo || ''} onChange={ev => {
                      updateEvent(e.id, {
                        brandLogo: ev.target.value
                      });
                      flashSaved(e.id);
                    }} className="bg-gray-800 border-gray-700 text-white text-sm" data-id="element-1622" data-mp="2t3lowcoqg5t3" />
                            <Input placeholder="Instagram URL" value={e.instagramUrl || ''} onChange={ev => {
                      updateEvent(e.id, {
                        instagramUrl: ev.target.value
                      });
                      flashSaved(e.id);
                    }} className="bg-gray-800 border-gray-700 text-white text-sm" data-id="element-1623" data-mp="iicoqpprvv2m" />
                            <Input placeholder="Website URL" value={e.websiteUrl || ''} onChange={ev => {
                      updateEvent(e.id, {
                        websiteUrl: ev.target.value
                      });
                      flashSaved(e.id);
                    }} className="bg-gray-800 border-gray-700 text-white text-sm" data-id="element-1624" data-mp="3mwj7308huk3w" />
                          </div>
                        </div>
                        <div data-id="element-1625" data-mp="2f7ivomt0t9o3">
                          <h4 className="text-xs font-bold text-gray-400 uppercase mb-2" data-id="element-1626" data-mp="2f8c5jwna0fag">
                            Products ({e.products?.length || 0})
                          </h4>
                          <ProductEditor products={e.products || []} onChange={ps => {
                    updateEvent(e.id, {
                      products: ps
                    });
                    flashSaved(e.id);
                  }} max={4} data-id="element-1627" data-mp="1nhxyokdok38a" />
                        </div>
                        <div data-id="element-1628" data-mp="37yu54nosjyj4">
                          <h4 className="text-xs font-bold text-gray-400 uppercase mb-2" data-id="element-1629" data-mp="to72casmrx5b">
                            Questions ({e.customQuestions?.length || 0})
                          </h4>
                          <QuestionEditor questions={e.customQuestions || []} onChange={qs => {
                    updateEvent(e.id, {
                      customQuestions: qs
                    });
                    flashSaved(e.id);
                  }} max={5} data-id="element-1630" data-mp="kok0jnvzopbq" />
                        </div>
                      </div>}
                  </div>

                  {/* Live amount controls + delete */}
                  <div className="flex items-center justify-between mt-4" data-id="element-1631" data-mp="1wbtt5g2um21n">
                    <div className="flex items-center space-x-2" data-id="element-1632" data-mp="1w9pga8mz1sp8">
                      <span className="text-xs text-gray-500 uppercase font-bold mr-1" data-id="element-1633" data-mp="21321s9wctjdr">
                        Amount
                      </span>
                      <button onClick={() => adjustAmount(e.id, -10)} className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 flex items-center justify-center transition-colors" aria-label="Decrease amount by 10" data-id="element-1634" data-mp="25z63gqiraw2a">
                        <Minus className="w-4 h-4" data-id="element-1635" data-mp="1rzmww25lfss3" />
                      </button>
                      <span className="font-bold text-white w-12 text-center" data-id="element-1636" data-mp="1n84xvjn4s29r">
                        {e.totalBoxes}
                      </span>
                      <button onClick={() => adjustAmount(e.id, 10)} className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 flex items-center justify-center transition-colors" aria-label="Increase amount by 10" data-id="element-1637" data-mp="1ia3hx7l04j2i">
                        <Plus className="w-4 h-4" data-id="element-1638" data-mp="14c6i82b93wjm" />
                      </button>
                    </div>
                    <button onClick={() => handleDeleteDrop(e.id, e.title)} className="w-8 h-8 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 flex items-center justify-center transition-colors" aria-label={`Delete ${e.title}`} data-id="element-1639" data-mp="3jyr3lzrr9acw">
                      <Trash2 className="w-4 h-4" data-id="element-1640" data-mp="2w9y63yvhiug6" />
                    </button>
                  </div>

                  {/* Refill / restock: adds to total + remaining (keeps redeemed) */}
                  <div className="mt-3 pt-3 border-t border-gray-800" data-id="element-1641" data-mp="366ufs7x4dmmb">
                    <label className="text-xs text-gray-500 uppercase font-bold mb-1.5 block" data-id="element-1642" data-mp="365jgolemd31j">
                      Refill / restock
                    </label>
                    <div className="flex items-center gap-2" data-id="element-1643" data-mp="2di5p72towzot">
                      <Input type="number" min="0" placeholder="Add boxes" value={refillAmounts[e.id] || ''} onChange={ev => setRefillAmounts(prev => ({
                  ...prev,
                  [e.id]: Math.max(0, Number(ev.target.value))
                }))} className="bg-gray-800 border-gray-700 text-white flex-1" data-id="element-1644" data-mp="2ih8gzlknt9eu" />
                      <Button type="button" variant="secondary" onClick={() => handleRefill(e.id)} disabled={!(refillAmounts[e.id] > 0)} className="flex items-center gap-1.5 text-xs py-2 whitespace-nowrap disabled:opacity-50" data-id="element-1645" data-mp="12qg2os4b643e">
                        <Plus className="w-3.5 h-3.5" data-id="element-1646" data-mp="3ihu46kficx94" /> Refill
                      </Button>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1.5" data-id="element-1647" data-mp="3dkvc07fen0dk">
                      Adds to both total and remaining boxes — redeemed boxes
                      are untouched.
                    </p>
                  </div>
                </div>)}
            </div>
          </div>}

        {/* TOOLS TAB */}
        {tab === 'tools' && <div className="space-y-6 max-w-3xl" data-id="element-1648" data-mp="1o86uc3jyan2z">
            {/* Automated testing bot — runs the full platform self-test suite */}
            <TestBot data-id="element-1649" data-mp="1acbgic4es26g" />

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-start space-x-3" data-id="element-1650" data-mp="1f9l3zm88m3rn">
              <DatabaseBackup className="w-5 h-5 text-yellow-400 mt-0.5 shrink-0" data-id="element-1651" data-mp="11a257k1bg4s0" />
              <div className="text-sm" data-id="element-1652" data-mp="15acf37s8bfc5">
                <p className="font-bold text-yellow-300" data-id="element-1653" data-mp="15bdzi2v4xe6j">
                  Test / mock data generator
                </p>
                <p className="text-gray-400 mt-1" data-id="element-1654" data-mp="2vlgzme59use3">
                  Populate Supabase with realistic data to pre-launch test every
                  dashboard, chart and flow. This writes real rows to your
                  connected database — use a test project, then erase the data
                  when done.
                </p>
              </div>
            </div>

            {/* Seed drops — configurable campaigns / brands / questions */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800" data-id="element-1655" data-mp="3un41396qcetw">
              <h3 className="font-bold text-white mb-1" data-id="element-1656" data-mp="v7guk68w1l79">
                Campaigns &amp; Drops
              </h3>
              <p className="text-sm text-gray-400 mb-4" data-id="element-1657" data-mp="r53p7iy1zpb0">
                Generate campaigns, each spanning several brands (one drop per
                brand). Brand socials, website and logo are filled in
                automatically, and each drop gets random brand-aware catch
                questions.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4" data-id="element-1658" data-mp="1ewhfljcn9atc">
                <div data-id="element-1659" data-mp="1a36jac5gkcfo">
                  <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1660" data-mp="1a2n3b0zdby4a">
                    Campaigns
                  </label>
                  <Input type="number" min="1" max="8" value={dropConfig.campaigns} onChange={e => setDropConfig(c => ({
                ...c,
                campaigns: Math.max(1, Math.min(8, Number(e.target.value) || 1))
              }))} className="bg-gray-800 border-gray-700 text-white" data-id="element-1661" data-mp="1me78plzhuygm" />
                </div>
                <div data-id="element-1662" data-mp="2f3mkwf2waxe4">
                  <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1663" data-mp="2f235eqp2cqde">
                    Brands / campaign
                  </label>
                  <Input type="number" min="1" max="6" value={dropConfig.brandsPerCampaign} onChange={e => setDropConfig(c => ({
                ...c,
                brandsPerCampaign: Math.max(1, Math.min(6, Number(e.target.value) || 1))
              }))} className="bg-gray-800 border-gray-700 text-white" data-id="element-1664" data-mp="2o3suhpvh3d80" />
                </div>
                <div data-id="element-1665" data-mp="2lpoy0kuw2mwe">
                  <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1666" data-mp="2lq89biq1ad1g">
                    Boxes / drop
                  </label>
                  <Input type="number" min="1" max="2000" value={dropConfig.boxesPerDrop} onChange={e => setDropConfig(c => ({
                ...c,
                boxesPerDrop: Math.max(1, Math.min(2000, Number(e.target.value) || 1))
              }))} className="bg-gray-800 border-gray-700 text-white" data-id="element-1667" data-mp="30fw5k5jcylib" />
                </div>
                <div data-id="element-1668" data-mp="dhaaujsamv36">
                  <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1669" data-mp="dili4tky6kz1">
                    Questions / drop
                  </label>
                  <Input type="number" min="0" max="5" value={dropConfig.questionsPerDrop} onChange={e => setDropConfig(c => ({
                ...c,
                questionsPerDrop: Math.max(0, Math.min(5, Number(e.target.value) || 0))
              }))} className="bg-gray-800 border-gray-700 text-white" data-id="element-1670" data-mp="115bizrlk89i2" />
                </div>
                <div className="col-span-2 sm:col-span-2" data-id="element-1671" data-mp="1etfezt0rmm9p">
                  <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1672" data-mp="1esx9e5s7qvp4">
                    Verification method
                  </label>
                  <select value={dropConfig.verificationMethod} onChange={e => setDropConfig(c => ({
                ...c,
                verificationMethod: e.target.value as VerificationMethod
              }))} className="w-full h-10 rounded-md bg-gray-800 border border-gray-700 text-white px-3 text-sm" data-id="element-1673" data-mp="1oiqi8z0z0wpo">
                    {VERIFICATION_METHODS.map(m => <option key={m.value} value={m.value} data-id="element-1674" data-mp="2lbc6xhi2vj23">
                        {m.label}
                      </option>)}
                  </select>
                </div>
              </div>

              <p className="text-xs text-gray-500 mb-3" data-id="element-1675" data-mp="2sss9nvfz0xj5">
                Will create{' '}
                <span className="text-gray-300 font-semibold" data-id="element-1676" data-mp="2sqfiqss8jcty">
                  {dropConfig.campaigns * dropConfig.brandsPerCampaign}
                </span>{' '}
                drops total.
              </p>

              <Button onClick={() => {
            if (isSeeding) return;
            setSeedLog([]);
            seedDrops();
            loadSheetData();
          }} disabled={isSeeding} className="w-full" data-id="element-1677" data-mp="37hoeljmqdufj">
                <Plus className="w-4 h-4 mr-2" data-id="element-1678" data-mp="32qy8u5ae7pkb" /> Generate Campaigns &amp; Drops
              </Button>
            </div>

            {/* Seed full dataset — configurable users + funnel */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800" data-id="element-1679" data-mp="3qepmbc7o68n6">
              <h3 className="font-bold text-white mb-1" data-id="element-1680" data-mp="3v8k4gb7mmfk5">
                Users + Catches + Reviews
              </h3>
              <p className="text-sm text-gray-400 mb-4" data-id="element-1681" data-mp="8vha9g8gwix4">
                Generates users with varied demographics, referrals, partner
                codes and answers, plus catches across the full funnel (reserved
                → redeemed → reviewed) and product reviews. Uses your existing
                drops, or creates the configured drops first if you have none.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4" data-id="element-1682" data-mp="3m6gd2v045b2s">
                <div data-id="element-1683" data-mp="3hc2ekjq5mntw">
                  <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1684" data-mp="3hbkax215x7by">
                    Users
                  </label>
                  <Input type="number" min="1" max="1000" value={userConfig.count} onChange={e => setUserConfig(c => ({
                ...c,
                count: Math.max(1, Math.min(1000, Number(e.target.value) || 0))
              }))} className="bg-gray-800 border-gray-700 text-white" data-id="element-1685" data-mp="37g8sa8mmvfyx" />
                </div>
                {([['partnerCodeRate', 'Partner code %'], ['referralRate', 'Referred %'], ['redeemRate', 'Redeemed %'], ['reviewRate', 'Reviewed %']] as [keyof UserGenConfig, string][]).map(([key, label]) => <div key={key} data-id="element-1686" data-mp="144ud4o0g70zg">
                    <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1687" data-mp="18y70yvxaz3r5">
                      {label}
                    </label>
                    <Input type="number" min="0" max="100" value={Math.round((userConfig[key] as number) * 100)} onChange={e => setUserConfig(c => ({
                ...c,
                [key]: Math.max(0, Math.min(100, Number(e.target.value) || 0)) / 100
              }))} className="bg-gray-800 border-gray-700 text-white" data-id="element-1688" data-mp="bhzvrzyxghq7" />
                  </div>)}
              </div>

              {/* Demographic filters — empty = use all options */}
              <div className="space-y-3 mb-4" data-id="element-1689" data-mp="3at3c6vn4c363">
                {([['cities', 'Cities', CITY_OPTIONS], ['ageRanges', 'Age ranges', AGE_OPTIONS], ['interests', 'Interests', INTEREST_OPTIONS]] as [keyof UserGenConfig, string, string[]][]).map(([key, label, options]) => {
              const selected = userConfig[key] as string[] || [];
              return <div key={key} data-id="element-1690" data-mp="16cg9u7baoiuo">
                      <label className="text-xs text-gray-500 uppercase font-bold mb-1.5 block" data-id="element-1691" data-mp="sm3328a53a6x">
                        {label}{' '}
                        <span className="text-gray-600 normal-case font-normal" data-id="element-1692" data-mp="xjmu68rxrhga">
                          {selected.length === 0 ? '(all)' : `(${selected.length} selected)`}
                        </span>
                      </label>
                      <div className="flex flex-wrap gap-2" data-id="element-1693" data-mp="9o0mmhvnqlkq">
                        {options.map(opt => {
                    const active = selected.includes(opt);
                    return <button key={opt} type="button" onClick={() => setUserConfig(c => ({
                      ...c,
                      [key]: toggleInList(c[key] as string[] | undefined, opt)
                    }))} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${active ? 'bg-brand-blue text-white' : 'bg-gray-800 text-gray-400 hover:text-white border border-gray-700'}`} data-id="element-1694" data-mp="242baan0in9ox">
                              {opt}
                            </button>;
                  })}
                      </div>
                    </div>;
            })}
              </div>

              <div className="mb-4" data-id="element-1695" data-mp="391hl8ltfjoqr">
                <Button onClick={seedFullDataset} disabled={isSeeding} className="w-full" data-id="element-1696" data-mp="3496mvnec8b0b">
                  {isSeeding ? <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" data-id="element-1697" data-mp="3giwah2yhnzcs" />
                      Seeding…
                    </> : <>
                      <DatabaseBackup className="w-4 h-4 mr-2" data-id="element-1698" data-mp="3qhlsqwuarltk" />
                      Generate {userConfig.count} Users + Activity
                    </>}
                </Button>
              </div>

              {isSeeding && seedProgress.total > 0 && <div className="mb-4" data-id="element-1699" data-mp="3b2wd2bk872b">
                  <div className="flex justify-between text-xs text-gray-400 mb-1" data-id="element-1700" data-mp="832t6lwc2juo">
                    <span data-id="element-1701" data-mp="d2pgjo5vlgh7">Writing rows…</span>
                    <span data-id="element-1702" data-mp="d1e46zyd70eh">
                      {seedProgress.done} / {seedProgress.total}
                    </span>
                  </div>
                  <div className="w-full bg-gray-800 h-2 rounded-full overflow-hidden" data-id="element-1703" data-mp="hzz571lfxo3z">
                    <div className="bg-brand-yellow h-full transition-all duration-300" style={{
                width: `${seedProgress.total > 0 ? seedProgress.done / seedProgress.total * 100 : 0}%`
              }} data-id="element-1704" data-mp="m1azvlj68zj1" />
                  </div>
                </div>}

              {seedLog.length > 0 && <div className="bg-gray-950 rounded-lg border border-gray-800 p-3 max-h-48 overflow-y-auto" data-id="element-1705" data-mp="1cbm2tup8kixn">
                  <ul className="space-y-1 text-xs font-mono text-gray-400" data-id="element-1706" data-mp="jorl35jszxnv">
                    {seedLog.map((line, i) => <li key={i} data-id="element-1707" data-mp="ydxhklr9b9s2" data-mpi={i}>{line}</li>)}
                  </ul>
                </div>}
            </div>

            {/* Connection test */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800" data-id="element-1708" data-mp="5x39r4hdpd7y">
              <h3 className="font-bold text-white mb-1" data-id="element-1709" data-mp="5v08tpeotzog">Connection Test</h3>
              <p className="text-sm text-gray-400 mb-4" data-id="element-1710" data-mp="xqlcfauzy39">
                Harmless read against the Supabase database to confirm the app
                can reach it.
              </p>
              <Button variant="secondary" onClick={runConnectionTest} disabled={isTesting} className="w-full" data-id="element-1711" data-mp="2hd3qsy953ct4">
                {isTesting ? 'Testing…' : 'Run Connection Test'}
              </Button>
              {testResult && <p className={`mt-3 text-sm font-medium ${testResult.ok ? 'text-green-400' : 'text-red-400'}`} data-id="element-1712" data-mp="2vcmmusooexp2">
                  {testResult.ok ? `✓ Success — Supabase is reachable.` : `✗ Failed (${testResult.status || 'no response'})`}
                </p>}
            </div>

            {/* Security — change the Supabase-stored login credentials */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800" data-id="element-1713" data-mp="1fvcokkejdsg6">
              <h3 className="font-bold text-white mb-1 flex items-center" data-id="element-1714" data-mp="1ft00rqm3knv7">
                <ShieldCheck className="w-5 h-5 mr-2 text-brand-blue" data-id="element-1715" data-mp="dxl2bzi9elii" />
                Login Credentials
              </h3>
              <p className="text-sm text-gray-400 mb-4" data-id="element-1716" data-mp="iz0knjt1ydx7">
                The admin, brand and partner passwords are stored in Supabase,
                so a change here applies on every device. Leave a field blank to
                keep its current password.
              </p>
              <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1717" data-mp="3reu2q3317xgd">
                New admin password
              </label>
              <Input type="password" placeholder="Leave blank to keep current" value={securityForm.adminPassword} onChange={e => setSecurityForm(f => ({
            ...f,
            adminPassword: e.target.value
          }))} className="bg-gray-800 border-gray-700 text-white mb-3" data-id="element-1718" data-mp="48jjm1kbxq0u" />
              <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1719" data-mp="35424dmjoutvt">
                New brand password
              </label>
              <Input type="password" placeholder="Leave blank to keep current" value={securityForm.brandPassword} onChange={e => setSecurityForm(f => ({
            ...f,
            brandPassword: e.target.value
          }))} className="bg-gray-800 border-gray-700 text-white mb-3" data-id="element-1720" data-mp="2w3vsu67jw3za" />
              <label className="text-xs text-gray-500 uppercase font-bold mb-1 block" data-id="element-1721" data-mp="2cgpry578s2g8">
                New partner password
              </label>
              <Input type="password" placeholder="Leave blank to keep current" value={securityForm.partnerPassword} onChange={e => setSecurityForm(f => ({
            ...f,
            partnerPassword: e.target.value
          }))} className="bg-gray-800 border-gray-700 text-white mb-2" data-id="element-1722" data-mp="28ci40a8lyqj2" />
              <p className="text-[11px] text-gray-500 mb-4" data-id="element-1723" data-mp="1eveq6l5qls4c">
                Partners open the portal at{' '}
                <span className="font-mono text-gray-400" data-id="element-1724" data-mp="1jtygsyv9zs31">/partner</span>.
              </p>
              <Button onClick={handleSaveSecurity} isLoading={isSavingSecurity} disabled={!securityForm.adminPassword.trim() && !securityForm.brandPassword.trim() && !securityForm.partnerPassword.trim()} className="w-full" data-id="element-1725" data-mp="27kbzvzvvuglh">
                Save credentials
              </Button>
              {securitySaved && <p className="mt-3 text-sm font-medium text-green-400 text-center" data-id="element-1726" data-mp="2v94yy57sx4kb">
                  ✓ Saved to Supabase
                </p>}
            </div>

            {/* DANGER ZONE — wipe all data before going live with real users */}
            <div className="bg-red-950/40 rounded-xl p-6 border border-red-900/60" data-id="element-1727" data-mp="eu9qil40c83f">
              <h3 className="font-bold text-red-300 mb-1 flex items-center" data-id="element-1728" data-mp="tiwf7dfiyc7b">
                <Trash2 className="w-4 h-4 mr-2" data-id="element-1729" data-mp="ole1l9qkb8sz" />
                Erase All Data
              </h3>
              <p className="text-sm text-red-200/70 mb-4" data-id="element-1730" data-mp="vtgak6xrzgj">
                Permanently deletes <strong data-id="element-1731" data-mp="3s1l4he15l20p">every</strong> user, catch and
                review — from both Supabase and this app. Use this once to clear
                test data before launching with real users. This cannot be
                undone.
              </p>
              <label className="text-xs text-red-300/80 uppercase font-bold mb-1 block" data-id="element-1732" data-mp="5sjx59qkez30">
                Type ERASE to confirm
              </label>
              <Input placeholder="ERASE" value={eraseConfirm} onChange={e => setEraseConfirm(e.target.value.toUpperCase())} className="bg-gray-900 border-red-900/60 text-white mb-4 font-mono tracking-widest" data-id="element-1733" data-mp="1h9joo35399vz" />
              <Button onClick={handleEraseAll} disabled={eraseConfirm !== 'ERASE' || isErasing} className="w-full bg-red-600 hover:bg-red-700 border-none disabled:bg-red-900/40 disabled:text-red-300/50" data-id="element-1734" data-mp="2lk4ypqea1jav">
                {isErasing ? <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" data-id="element-1735" data-mp="2zil1j690qpnq" />
                    Erasing…
                  </> : <>
                    <Trash2 className="w-4 h-4 mr-2" data-id="element-1736" data-mp="399fx5185n1lu" />
                    Erase Everything
                  </>}
              </Button>
            </div>
          </div>}
      </div>
    </div>;
}